import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import {
  Search,
  Filter,
  Upload,
  FolderPlus,
  File,
  FileText,
  FileImage,
  FileVideo,
  FileArchive,
  MoreVertical,
  Download,
  Trash2,
  Share2,
  Eye,
  Edit2,
  X,
  Plus,
  Grid,
  List,
  Loader2,
  FileCode,
  Music,
  FileBadge,
  FileAudio,
  FileCog,
  Tablet,
  FileDigit,
  FileJson,
  FileKey,
  FilePenLine,
  FileSpreadsheet,
  FileLock,
  Microscope,
  Code,
  Home,
  ChevronRight,
  ArrowUp,
  Clock,
  Timer,
  Database,
  FileQuestion,
  HardDrive,
  Loader,
  ChevronDown,
  SortDesc,
  Calendar,
  CheckSquare,
  BarChart2,
  PieChart,
  Activity,
  Layers,
  ArrowLeft,
  ArrowRight,
  Sliders,
  CalendarRange,
  ArrowDownUp,
  Check,
  ChevronUp,
  XCircle,
  FolderOpen,
  Upload as UploadIcon,
  AlertCircle,
  Folder,
  Info,
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-hot-toast';
import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend 
} from 'recharts';
import SharedFiles from './SharedFiles';
import { formatDistanceToNow } from 'date-fns';
import tr from 'date-fns/locale/tr';

// Supabase istemcisini başlat
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Storage bucket tanımlamaları
// Daha modüler bir yapı için storage ile ilgili config'leri burada tanımlıyoruz
const storageConfig = {
  bucketName: import.meta.env.VITE_FILE_SUPABASE_BUCKET_NAME || 'files',
  supabaseUrl: import.meta.env.VITE_FILE_SUPABASE_URL || supabaseUrl,
  supabaseKey: import.meta.env.VITE_FILE_SUPABASE_ANON_KEY || supabaseAnonKey
};

// Özel storage client - özel ayarlar için (isteğe bağlı)
const storageClient = createClient(
  storageConfig.supabaseUrl,
  storageConfig.supabaseKey
);

// Yerel değişken yerine config değişkenini kullan
const BUCKET_NAME = storageConfig.bucketName;

// Storage işlemleri için yardımcı servis
// Bu servis, tüm dosya işlemlerini kapsayan merkezi bir erişim noktası sağlar
const storageService = {
  client: storageClient,
  bucketName: BUCKET_NAME,
  
  // Dosya işlemleri
  getPublicUrl: (path) => {
    return storageClient.storage.from(BUCKET_NAME).getPublicUrl(path);
  },
  
  upload: (path, file, options = {}) => {
    return storageClient.storage.from(BUCKET_NAME).upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      ...options
    });
  },
  
  list: (path, options = {}) => {
    return storageClient.storage.from(BUCKET_NAME).list(path, options);
  },
  
  remove: (paths) => {
    return storageClient.storage.from(BUCKET_NAME).remove(paths);
  },
  
  download: (path) => {
    return storageClient.storage.from(BUCKET_NAME).download(path);
  },
  
  // Dosya URL'lerini oluşturma
  getDownloadUrl: async (filePath) => {
    const { data, error } = await storageClient.storage
      .from(BUCKET_NAME)
      .getPublicUrl(filePath);
      
    if (error) throw error;
    return data.publicUrl;
  }
};

const FILE_TYPES = {
  document: {
    icon: FileText,
    color: 'blue',
    extensions: ['doc', 'docx', 'txt', 'rtf', 'odt'],
  },
  spreadsheet: {
    icon: FileSpreadsheet,
    color: 'green',
    extensions: ['xls', 'xlsx', 'csv', 'ods'],
  },
  presentation: {
    icon: FilePenLine,
    color: 'orange',
    extensions: ['ppt', 'pptx', 'odp', 'key'],
  },
  pdf: {
    icon: FileBadge,
    color: 'red',
    extensions: ['pdf'],
  },
  image: {
    icon: FileImage,
    color: 'pink',
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff', 'ico'],
  },
  video: {
    icon: FileVideo,
    color: 'purple',
    extensions: ['mp4', 'avi', 'mov', 'webm', 'mkv', 'flv', 'wmv', 'mpg', 'mpeg'],
  },
  audio: {
    icon: FileAudio,
    color: 'indigo',
    extensions: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'wma'],
  },
  archive: {
    icon: FileArchive,
    color: 'yellow',
    extensions: ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'iso'],
  },
  code: {
    icon: FileCode,
    color: 'teal',
    extensions: ['html', 'css', 'js', 'jsx', 'ts', 'tsx', 'php', 'py', 'java', 'c', 'cpp', 'h', 'cs', 'rb', 'go', 'swift', 'kt'],
  },
  executable: {
    icon: FileCog,
    color: 'black',
    extensions: ['exe', 'msi', 'app', 'dmg', 'bat', 'sh', 'com', 'cmd'],
  },
  '3d': {
    icon: Tablet,
    color: 'violet',
    extensions: ['obj', 'fbx', 'blend', 'stl', '3ds', 'dae', 'gltf', 'glb', 'max', 'ma', 'mb', 'c4d'],
  },
  database: {
    icon: FileDigit,
    color: 'amber',
    extensions: ['db', 'sql', 'sqlite', 'mdb', 'accdb', 'dbf', 'json', 'xml'],
  },
  font: {
    icon: FileJson,
    color: 'cyan',
    extensions: ['ttf', 'otf', 'woff', 'woff2', 'eot'],
  },
  system: {
    icon: FileKey,
    color: 'rose',
    extensions: ['dll', 'sys', 'ini', 'cfg', 'config', 'reg', 'log'],
  },
  encrypted: {
    icon: FileLock,
    color: 'slate',
    extensions: ['gpg', 'pgp', 'asc', 'enc', 'pem', 'p12', 'pfx', 'key', 'keystore', 'jks'],
  },
  scientific: {
    icon: Microscope,
    color: 'emerald',
    extensions: ['mat', 'nb', 'r', 'rmd', 'npy', 'ipynb', 'sav', 'spss', 'sas'],
  },
  other: {
    icon: File,
    color: 'green',
    extensions: [],
  },
};

// Dosya tipini belirleme fonksiyonu
const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  
  for (const [type, data] of Object.entries(FILE_TYPES)) {
    if (data.extensions.includes(extension)) {
      return type;
    }
  }
  
  return 'other';
};

// Dosya boyutunu formatla
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Türkçe karakterleri ve özel karakterleri güvenli karakterlere dönüştüren fonksiyon
const sanitizeFileName = (fileName) => {
  // Türkçe karakter dönüşüm tablosu
  const turkishMap = {
    'ç': 'c', 'Ç': 'C',
    'ğ': 'g', 'Ğ': 'G',
    'ı': 'i', 'İ': 'I',
    'ö': 'o', 'Ö': 'O',
    'ş': 's', 'Ş': 'S',
    'ü': 'u', 'Ü': 'U'
  };
  
  // Dosya adı ve uzantıyı ayır
  const lastDotIndex = fileName.lastIndexOf('.');
  const name = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
  const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';
  
  // Dosya adındaki Türkçe karakterleri değiştir
  let sanitizedName = name.replace(/[çÇğĞıİöÖşŞüÜ]/g, match => turkishMap[match] || match);
  
  // Özel karakterleri ve boşlukları alt çizgi ile değiştir (dosya sistemine uygun)
  sanitizedName = sanitizedName.replace(/[^a-zA-Z0-9._-]/g, '_');
  
  // Güvenli adı ve orijinal uzantıyı birleştir
  return sanitizedName + extension;
};

const Files = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showStats, setShowStats] = useState(false); // İstatistikler modalı için
  
  // Filtreleme durumları
  const [filters, setFilters] = useState({
    fileType: 'all',
    fileSize: 'all',
    dateAdded: 'all',
    sortBy: 'name_asc', // name_asc, name_desc, date_asc, date_desc, size_asc, size_desc
    // Slider için ek değerler
    fileSizeRange: [0, 100], // MB cinsinden
    dateRange: [0, 0], // Timestamp değerleri
    selectedFileTypes: [] // Çoklu dosya tipi seçimi için
  });
  
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showFileActions, setShowFileActions] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]); // Yüklenen dosyaları tutan state
  
  // Dosyaların tarih ve boyut aralıkları için state
  const [fileSizeBounds, setFileSizeBounds] = useState({
    min: 0, // Bayt cinsinden
    max: 100 * 1024 * 1024, // Varsayılan max 100MB
  });
  
  const [dateBounds, setDateBounds] = useState({
    oldest: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000), // Varsayılan 1 yıl öncesi
    newest: new Date(), // Şimdiki zaman
  });
  
  // Modal için ref
  const modalRef = useRef(null);
  // 3 nokta menüsü için ref
  const menuRef = useRef(null);
  
  // Dosya yükleme için
  const [isUploading, setIsUploading] = useState(false);
  
  // Sayfa dışına tıklandığında 3 nokta menüsünü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Menü açıksa ve menü dışına tıklanmışsa kapat
      if (
        showFileActions && 
        !event.target.closest('.file-action-menu') &&
        !event.target.closest('.file-menu-toggle')
      ) {
        setShowFileActions(null);
      }
    };
    
    // Click listener ekle
    document.addEventListener('mousedown', handleClickOutside);
    
    // Temizlik fonksiyonu
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFileActions]);
  
  // Dosya ekleme/çıkarma
  const addFiles = useCallback((newFiles) => {
    setUploadFiles(prev => [...prev, ...newFiles]);
  }, []);
  
  const removeFile = useCallback((index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  }, []);
  
  // Dosya seçimi ve sürükle-bırak fonksiyonları
  const getRootProps = useCallback(() => ({
    onClick: () => document.getElementById('file-upload-input').click(),
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDrop: (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFiles(Array.from(e.dataTransfer.files));
      }
    }
  }), [addFiles]);
  
  const getInputProps = useCallback(() => ({
    id: 'file-upload-input',
    type: 'file',
    multiple: true,
    style: { display: 'none' },
    onChange: (e) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(Array.from(e.target.files));
      }
    }
  }), [addFiles]);
  
  // Dosya yükleme işlemi başlatıldığında
  const startUpload = useCallback(() => {
    if (uploadFiles.length > 0) {
      setIsUploading(true);
      handleFileUpload(uploadFiles);
    }
  }, [uploadFiles]);
  
  // Dosya yükleme istatistikleri için state'ler
  const [uploadStats, setUploadStats] = useState({
    startTime: null,
    totalSize: 0,
    loadedSize: 0,
    speed: 0,
    remainingTime: 0,
    elapsedTime: 0,
    fileProgress: [], // Her dosya için ilerleme
    speedData: [],    // Hız hesaplama için veri noktaları
    lastUpdated: 0,    // Son güncelleme timestamp'i
    progress: 0,       // İlerleme yüzdesi
  });
  
  // Klasör sistemi için state'ler
  const [currentPath, setCurrentPath] = useState('/');
  const [pathHistory, setPathHistory] = useState(['/']);
  const [pathHistoryIndex, setPathHistoryIndex] = useState(0);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolderPath, setSelectedFolderPath] = useState(''); // Seçilen klasör yolu

  // Kullanıcı kimlik bilgisini almak için
  const [user, setUser] = useState(null);
  
  // Önceki değerleri izlemek için ref'ler
  const prevSpeedRef = React.useRef(0);
  const prevRemainingTimeRef = React.useRef(0);
  
  // Değerler değiştiğinde karşılaştırma yapmak için useEffect
  React.useEffect(() => {
    prevSpeedRef.current = uploadStats.speed;
    prevRemainingTimeRef.current = uploadStats.remainingTime;
  }, [uploadStats.speed, uploadStats.remainingTime]);
  
  // Önceki değerlere kolay erişim
  const prevSpeed = prevSpeedRef.current;
  const prevRemainingTime = prevRemainingTimeRef.current;
  
  // Şu anda yüklenen dosyanın bilgilerini hesapla
  const currentFileIndex = uploadStats.fileProgress.findIndex(
    file => file && file.loaded < file.total
  );
  
  const currentFileName = currentFileIndex >= 0 && 
    uploadFiles.length > currentFileIndex ? 
    uploadFiles[currentFileIndex]?.name : '';

  // Current path'i breadcrumb olarak parçalara ayır
  const pathSegments = useMemo(() => {
    if (currentPath === '/') return [];
    
    // Önce ve sondaki '/' karakterlerini kaldır ve boş değilse path'i parçalara ayır
    const cleanPath = currentPath.replace(/^\/+|\/+$/g, '');
    if (!cleanPath) return [];
    
    // Yolu parçalara ayır ve her bir parça için bir nesne oluştur
    const segments = cleanPath.split('/').map((segment, index, arr) => {
      // Her segment için tam yolu hesapla (önceki segmentleri de içeren)
      const pathUpToSegment = '/' + arr.slice(0, index + 1).join('/') + '/';
      
      return {
        id: `segment-${index}`,
        file_name: segment,
        fullPath: pathUpToSegment
      };
    });
    
    return segments;
  }, [currentPath]);
  
  // Breadcrumb'a tıklandığında belirli bir yola git
  const navigateToBreadcrumb = (index) => {
    const targetPath = pathSegments[index].fullPath;
    
    // Gezinme geçmişini güncelle
    const newHistory = [...pathHistory.slice(0, pathHistoryIndex + 1), targetPath];
    setPathHistory(newHistory);
    setPathHistoryIndex(newHistory.length - 1);
    
    // Yeni klasöre git
    setCurrentPath(targetPath);
  };

  // fetchFiles işlevini memoize et
  const fetchFilesFromDB = useCallback(async () => {
    try {
      setLoading(true);
      
      // Eğer kullanıcı oturum açmamışsa, boş dizi döndür
      if (!user) {
        setFiles([]);
        return;
      }
      
      // Veritabanından sadece kullanıcının kendi dosyalarını al
      // ve mevcut klasör yoluna göre filtrele
      const { data: fileRecords, error } = await supabase
        .from('storage_files')
        .select('*')
        .eq('uploaded_by', user.id)
        .eq('folder_path', currentPath)
        .order('is_folder', { ascending: false }) // Önce klasörler gösterilsin
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Supabase bucket'tan URL'leri al
      const filesWithUrls = await Promise.all(
        fileRecords.map(async (file) => {
          // Eğer klasörse, URL almaya gerek yok
          if (file.is_folder) {
            // Klasör boyutu 0 ise veya henüz hesaplanmamışsa hesapla
            if (!file.file_size || file.file_size === 0) {
              // Boyutu bu aşamada sorgulama, FileCard bileşeni içinde yapılacak
              return file;
            }
            return file;
          }
          
          // storageService kullanarak dosya URL'ini al - createSignedUrl kullanma
          const { data } = await storageService.getPublicUrl(file.file_path);
          
          return {
            ...file,
            url: data?.publicUrl || null,
          };
        })
      );
      
      setFiles(filesWithUrls);
    } catch (error) {
      console.error('Dosyalar getirilirken hata oluştu:', error);
      toast.error('Dosyalar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [user, currentPath]);
  
  useEffect(() => {
    // Oturum durumunu kontrol et
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session) {
          setUser(session.user);
          // Kullanıcı oturum açtığında dosyaları getir
          // fetchFiles burada çağrılmayacak, user değişimi fetchFilesFromDB'yi tetikleyecek
        } else {
          setUser(null);
          // Kullanıcı çıkış yaptığında dosyaları temizle
          setFiles([]);
        }
      }
    );

    // Mevcut oturum varsa kullanıcıyı ayarla
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        // Mevcut oturum bilgisiyle dosyaları getir
        // fetchFiles burada çağrılmayacak, user değişimi fetchFilesFromDB'yi tetikleyecek
      }
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);
  
  // User değiştiğinde dosyaları otomatik olarak yükle
  useEffect(() => {
    if (user) {
      fetchFilesFromDB();
    }
  }, [user, fetchFilesFromDB]);

  // Yükleme devam ederken istatistikleri güncelleyen zamanlayıcı
  useEffect(() => {
    let timerId = null;
    
    if (uploadLoading && uploadStats.startTime) {
      // Daha sık güncelleme için 100ms'ye düşürdük (200ms yerine)
      timerId = setInterval(() => {
        // İstatistikleri sadece aktif yükleme varsa güncelle
        setUploadStats(prevStats => {
          // Son güncelleme zamanından beri çok az zaman geçtiyse güncelleme yapma (gereksiz render'ı önle)
          const now = Date.now();
          const timeSinceLastUpdate = now - prevStats.lastUpdated;
          
          // En az 100ms geçmiş olması lazım
          if (timeSinceLastUpdate < 100) {
            return prevStats;
          }
          
          const elapsedTime = (now - prevStats.startTime) / 1000; // saniye cinsinden
          
          // En son kaydedilen hız verileri
          const recentSpeedData = [...(prevStats.speedData || [])];
          
          // Toplam yüklenen boyut son güncellemeden beri değişmediyse, 
          // yeni bir veri noktası eklemeye gerek yok
          const needsUpdate = recentSpeedData.length === 0 || 
                             recentSpeedData[recentSpeedData.length - 1].loaded !== prevStats.loadedSize;
          
          if (needsUpdate) {
            // Yeni bir veri noktası ekle
            recentSpeedData.push({
              timestamp: now,
              loaded: prevStats.loadedSize
            });
          }
          
          // Son 3 saniyedeki verileri tut
          const filteredSpeedData = recentSpeedData.filter(item => now - item.timestamp < 3000);
          
          // Hızı hesapla (byte/saniye) - son 3 saniyedeki ortalama
          let speed = prevStats.speed; // Varsayılan olarak mevcut hızı kullan
          
          if (filteredSpeedData.length > 1) {
            const oldestData = filteredSpeedData[0];
            const timeDiff = (now - oldestData.timestamp) / 1000;
            const loadedDiff = prevStats.loadedSize - oldestData.loaded;
            
            if (timeDiff > 0) {
              // Düzgün bir değer almak için bazı sınırlamalar uygula
              const newSpeed = loadedDiff / timeDiff;
              
              // Ani değişimleri yumuşat
              speed = prevStats.speed > 0 
                ? (prevStats.speed * 0.7) + (newSpeed * 0.3)  // %70 eski, %30 yeni değer 
                : newSpeed;
            }
          }
          
          // Kalan süreyi hesapla
          const remainingBytes = prevStats.totalSize - prevStats.loadedSize;
          const remainingTime = speed > 0 ? remainingBytes / speed : 0;
          
          return {
            ...prevStats,
            elapsedTime,
            speed,
            remainingTime,
            speedData: filteredSpeedData,
            lastUpdated: now
          };
        });
      }, 100); // 100ms'de bir güncelleme
    }
    
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [uploadLoading, uploadStats.startTime]);

  // Filtreleri temizle
  const resetFilters = () => {
    setFilters({
      fileType: 'all',
      fileSize: 'all',
      dateAdded: 'all',
      sortBy: 'name_asc',
      fileSizeRange: [0, 100],
      dateRange: [0, 0],
      selectedFileTypes: []
    });
    setSearchQuery('');
  };
  
  // Filtreleri uygula - filteredFiles hesaplama
  const filteredFiles = useMemo(() => {
    // İlk olarak arama sorgusuna göre filtrele
    let result = files.filter(file =>
      file.file_name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Dosya tipine göre filtrele
    if (filters.fileType !== 'all') {
      if (filters.fileType === 'folder') {
        result = result.filter(file => file.is_folder);
      } else {
        result = result.filter(file => !file.is_folder && file.file_type === filters.fileType);
      }
    }
    
    // Dosya boyutuna göre filtrele
    if (filters.fileSize !== 'all') {
      const sizeRanges = {
        small: { min: 0, max: 100 * 1024 },         // 0 - 100KB
        medium: { min: 100 * 1024, max: 1024 * 1024 }, // 100KB - 1MB
        large: { min: 1024 * 1024, max: 10 * 1024 * 1024 }, // 1MB - 10MB
        xlarge: { min: 10 * 1024 * 1024, max: Infinity }  // 10MB+
      };
      
      const range = sizeRanges[filters.fileSize];
      result = result.filter(file => !file.is_folder && file.file_size >= range.min && file.file_size < range.max);
    }
    
    // Yüklenme tarihine göre filtrele
    if (filters.dateAdded !== 'all') {
      const now = new Date();
      let compareDate = new Date();
      
      if (filters.dateAdded === 'today') {
        compareDate.setHours(0, 0, 0, 0);
      } else if (filters.dateAdded === 'week') {
        compareDate.setDate(now.getDate() - 7);
      } else if (filters.dateAdded === 'month') {
        compareDate.setMonth(now.getMonth() - 1);
      } else if (filters.dateAdded === 'year') {
        compareDate.setFullYear(now.getFullYear() - 1);
      }
      
      result = result.filter(file => new Date(file.created_at) >= compareDate);
    }
    
    // Sıralamayı uygula
    switch (filters.sortBy) {
      case 'name_asc':
        result.sort((a, b) => a.file_name.localeCompare(b.file_name));
        break;
      case 'name_desc':
        result.sort((a, b) => b.file_name.localeCompare(a.file_name));
        break;
      case 'date_asc':
        result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'date_desc':
        result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'size_asc':
        result.sort((a, b) => a.file_size - b.file_size);
        break;
      case 'size_desc':
        result.sort((a, b) => b.file_size - a.file_size);
        break;
      default:
        // Varsayılan olarak klasörler önce gösterilsin
        result.sort((a, b) => {
          if (a.is_folder && !b.is_folder) return -1;
          if (!a.is_folder && b.is_folder) return 1;
          return a.file_name.localeCompare(b.file_name);
        });
    }
    
    return result;
  }, [files, searchQuery, filters]);
  
  // Klasörler her zaman önce gösterilsin
  const sortedFilteredFiles = useMemo(() => {
    // Eğer zaten dosya tipi filtrelemesi varsa, tekrar klasör filtrelemesi yapmaya gerek yok
    if (filters.fileType !== 'all') {
      return filteredFiles;
    }
    
    // Klasörler ve dosyaları ayır
    const folders = filteredFiles.filter(file => file.is_folder);
    const regularFiles = filteredFiles.filter(file => !file.is_folder);
    
    // Önce klasörler, sonra dosyalar
    return [...folders, ...regularFiles];
  }, [filteredFiles, filters.fileType]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Yükleme istatistiklerini güncelleyen fonksiyon
  const updateUploadStats = useCallback((fileIndex, loaded, total) => {
    setUploadStats(prevStats => {
      // console.log(`UpdateStats: Dosya ${fileIndex+1}, Yüklenen: ${loaded}, Toplam: ${total}`);
      
      const now = Date.now();
      const startTime = prevStats.startTime || now;
      const elapsedTime = (now - startTime) / 1000; // saniye cinsinden
      
      // Yüklenen dosya boyutunu güncelle - loaded değeri azalmayacak şekilde kontrol et
      const fileProgress = [...prevStats.fileProgress];
      const currentFileProgress = fileProgress[fileIndex] || { loaded: 0, total };
      
      // Yükleme değeri azalmamalı
      if (loaded < currentFileProgress.loaded) {
        loaded = currentFileProgress.loaded;
      }
      
      fileProgress[fileIndex] = { loaded, total };
      
      // Toplam yüklenen boyutu hesapla
      const loadedSize = fileProgress.reduce((sum, file) => sum + (file?.loaded || 0), 0);
      // console.log(`Toplam yüklenen: ${loadedSize} / Toplam boyut: ${prevStats.totalSize}`);
      
      // Toplam boyutu hesapla (ilk seferde)
      const totalSize = prevStats.totalSize || fileProgress.reduce((sum, file) => sum + (file?.total || 0), 0);
      
      // Son 3 saniyedeki hızı hesaplamak için hız verilerini saklama
      const speedData = [...(prevStats.speedData || [])];
      speedData.push({ 
        timestamp: now,
        loaded: loadedSize 
      });
      
      // Son 3 saniyedeki verileri tut
      const recentSpeedData = speedData.filter(item => now - item.timestamp < 3000);
      
      // Hızı hesapla (byte/saniye) - son 3 saniyedeki ortalama
      let speed = 0;
      if (recentSpeedData.length > 1) {
        const oldestData = recentSpeedData[0];
        const timeDiff = (now - oldestData.timestamp) / 1000;
        const loadedDiff = loadedSize - oldestData.loaded;
        
        if (timeDiff > 0) {
          speed = loadedDiff / timeDiff;
        }
      } else {
        // Eğer yeteri kadar veri yoksa, toplam ortalamayı kullan
        speed = elapsedTime > 0 ? loadedSize / elapsedTime : 0;
      }
      
      // Kalan süreyi hesapla
      const remainingBytes = totalSize - loadedSize;
      const remainingTime = speed > 0 ? remainingBytes / speed : 0;
      
      // İlerleme yüzdesini hesapla
      const progress = totalSize > 0 ? Math.round((loadedSize / totalSize) * 100) : 0;
      
      // Önceki ilerlemeyle karşılaştır ve daha düşükse önceki değeri kullan
      const newProgress = Math.max(progress, prevStats.progress || 0);
      
      // console.log(`İlerleme: ${newProgress}%, Hız: ${formatSpeed(speed)}, Kalan: ${formatTime(remainingTime)}`);
      
      // Upload progress'i doğrudan güncelle (ayrı bir state olduğu için)
      if (prevStats.progress !== newProgress) {
        setUploadProgress(newProgress);
      }
      
      // UI güncellemek için setTimeout ekleme (React'ın stateyi doğru güncellemesini zorlamak için)
      setTimeout(() => {
        // console.log("UI güncelleme zamanı - progress:", newProgress);
        setUploadProgress(prevProgress => {
          // Eğer değer değişmediyse force update için +0.1 ekle
          return prevProgress === newProgress ? newProgress + 0.1 : newProgress;
        });
      }, 20);
      
      return {
        startTime,
        totalSize,
        loadedSize,
        speed,
        remainingTime,
        elapsedTime,
        fileProgress,
        speedData: recentSpeedData, // Hız verilerini sakla
        lastUpdated: now, // Son güncelleme zamanını tut
        progress: newProgress, // İlerleme yüzdesini state'e kaydet
      };
    });
  }, []);
  
  // Okunaklı format fonksiyonları
  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const formatTime = (seconds) => {
    if (!seconds || !isFinite(seconds) || seconds === Infinity) return '--:--';
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  const formatSpeed = (bytesPerSecond) => {
    return `${formatSize(bytesPerSecond)}/s`;
  };

  // Yükleme tamamlandı durumu için state ekleyelim
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploadedFilesInfo, setUploadedFilesInfo] = useState([]);

  // Klasör boyutunu recursive olarak hesaplayan fonksiyon
  const calculateFolderSize = async (folderPath, userId) => {
    try {
      // Kök dizin için özel durum kontrolü
      if (folderPath === '/' || folderPath === '') {
        // Tüm kök dizin dosyalarını getir
        const { data: folderContents, error } = await supabase
          .from('storage_files')
          .select('*')
          .eq('uploaded_by', userId)
          .eq('folder_path', '/');

        if (error) throw error;
        if (!folderContents || folderContents.length === 0) return 0;

        // Tüm dosyaların boyutlarını topla
        let totalSize = 0;
        
        folderContents.forEach(item => {
          if (!item.is_folder) {
            totalSize += (item.file_size || 0);
          }
        });

        // Kök klasörün boyutunu güncelle
        const { error: updateError } = await supabase
          .from('storage_files')
          .update({ file_size: totalSize })
          .eq('file_path', '/')
          .eq('is_folder', true);

        if (updateError) {
          console.warn('Kök klasör boyutu güncellenirken hata:', updateError);
        }

        return totalSize;
      }

      // Normal klasörler için mevcut kod
      // Önce klasör içindeki tüm dosya ve klasörleri getir
      const { data: folderContents, error } = await supabase
        .from('storage_files')
        .select('*')
        .eq('uploaded_by', userId)
        .like('folder_path', `${folderPath}%`);

      if (error) throw error;
      if (!folderContents || folderContents.length === 0) return 0;

      // Tüm dosyaların boyutlarını topla
      let totalSize = 0;
      
      folderContents.forEach(item => {
        if (!item.is_folder) {
          totalSize += (item.file_size || 0);
        }
      });

      // Klasörün hesaplanan boyutunu güncelle
      const { error: updateError } = await supabase
        .from('storage_files')
        .update({ file_size: totalSize })
        .eq('file_path', folderPath)
        .eq('is_folder', true);

      if (updateError) {
        console.warn('Klasör boyutu güncellenirken hata:', updateError);
      }

      return totalSize;
    } catch (error) {
      console.error('Klasör boyutu hesaplanırken hata:', error);
      return 0;
    }
  };

  // Belirli bir klasör yoluna göre boyutu hesaplar veya önbellekten getirir
  const getFolderSize = async (folderPath) => {
    try {
      if (!user) return 0;
      
      // Kök dizin kontrolü
      if (folderPath === '/' || folderPath === '') {
        return calculateFolderSize('/', user.id);
      }
      
      // Önce klasörü veritabanından kontrol et
      const { data: folderData, error } = await supabase
        .from('storage_files')
        .select('file_size')
        .eq('file_path', folderPath)
        .eq('is_folder', true)
        .single();

      if (error) {
        // Klasör bulunamadıysa veya başka bir hata olduysa hesapla
        return calculateFolderSize(folderPath, user.id);
      }

      // Eğer boyut bilgisi varsa ve 0 değilse, onu kullan
      if (folderData && folderData.file_size > 0) {
        return folderData.file_size;
      }

      // Yoksa veya 0 ise, hesapla
      return calculateFolderSize(folderPath, user.id);
    } catch (error) {
      console.error('Klasör boyutu alınırken hata:', error);
      return 0;
    }
  };

  // Tüm klasörlerin boyutlarını güncelleme fonksiyonu
  const updateAllFolderSizes = async () => {
    if (!user) return;
    
    try {
      // Tüm klasörleri getir
      const { data: allFolders, error } = await supabase
        .from('storage_files')
        .select('file_path')
        .eq('uploaded_by', user.id)
        .eq('is_folder', true);

      if (error) throw error;
      if (!allFolders || allFolders.length === 0) return;

      // En uzun yoldan başlayarak klasörleri sırala (alt klasörlerden başa doğru)
      allFolders.sort((a, b) => b.file_path.length - a.file_path.length);

      // Her klasör için boyutu hesapla
      for (const folder of allFolders) {
        await calculateFolderSize(folder.file_path, user.id);
      }
      
      // Dosya listesini güncelle
      await fetchFilesFromDB();
      
    } catch (error) {
      console.error('Klasör boyutları güncellenirken hata:', error);
    }
  };

  // Dosya yükleme veya silme işlemlerine klasör güncelleme ekle
  const handleFileUpload = async (uploadedFiles) => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;
    
    try {
      setShowUploadModal(true);
      setUploadLoading(true);
      setIsUploading(true);
      setUploadProgress(0);
      setUploadComplete(false);
      setUploadFiles(Array.from(uploadedFiles));

      // Toplam boyutu hesapla
      const totalSize = Array.from(uploadedFiles).reduce((total, file) => total + file.size, 0);
      // console.log(`Toplam yüklenecek boyut: ${formatSize(totalSize)}`);
      
      // Yükleme başlangıç zamanını ayarla
      const startTime = Date.now();
      
      // Yükleme istatistiklerini sıfırla
      setUploadStats({
        startTime,
        totalSize,
        loadedSize: 0,
        speed: 0,
        remainingTime: 0,
        elapsedTime: 0,
        fileProgress: Array.from(uploadedFiles).map(file => ({ loaded: 0, total: file.size })),
        speedData: [],
        lastUpdated: startTime,
        progress: 0,
      });
      
      // console.log('Yükleme başladı:', uploadedFiles.length, 'dosya');
      
      // Manuel olarak yükleme istatistiklerini güncelleme test fonksiyonu
      let testUpdateInterval;
      const testUpdates = () => {
        //console.log("Manuel test güncelleme başlatıldı");
        // Önceki ilerleme değerini takip eden değişken
        let previousProgress = 0;
        
        testUpdateInterval = setInterval(() => {
          const elapsed = (Date.now() - startTime) / 1000;
          
          // Daha az ama daha belirgin adımlarla ilerlesin - %2'lik artışlar ile
          // %10/saniye yerine %4/saniye hızla ilerlesin
          let simulatedProgress = Math.min(95, Math.floor(elapsed * 4) * 2);
          
          // Yükleme yaparken ilerleme değeri azalmamalı, önceki değerle karşılaştır
          if (simulatedProgress < previousProgress) {
            simulatedProgress = previousProgress; // Geri gitmesin
          } else if (simulatedProgress > previousProgress) {
            // Sadece değer değiştiğinde log yaz
            // console.log(`Test İlerleme: %${simulatedProgress}, Yüklenen: ${formatSize(Math.floor(totalSize * (simulatedProgress / 100)))}`);
            previousProgress = simulatedProgress; // Yeni değeri kaydet
          }
          
          // Test için toplam yüklenmiş boyutu hesapla
          const simulatedLoaded = Math.floor(totalSize * (simulatedProgress / 100));
          
          // Direkt progress'i güncelle - tam sayı olarak göster
          setUploadProgress(simulatedProgress);
          
          // Tüm dosyalarda eşit ilerleme göster
          const fileProgress = Array.from(uploadedFiles).map(file => ({ 
            loaded: Math.floor(file.size * (simulatedProgress / 100)), 
            total: file.size 
          }));
          
          // State'i güncelle
          setUploadStats(prev => ({
            ...prev,
            loadedSize: simulatedLoaded,
            speed: simulatedLoaded / elapsed,
            elapsedTime: elapsed,
            remainingTime: elapsed * ((100 - simulatedProgress) / simulatedProgress),
            fileProgress,
            progress: simulatedProgress
          }));
          
          if (simulatedProgress >= 95) {
            clearInterval(testUpdateInterval);
            // console.log("Manuel test güncelleme tamamlandı");
          }
        }, 500);
      };
      
      // Test güncellemelerini başlat
      testUpdates();
      
      // Oturum bilgisini al
      const { data: { session }} = await supabase.auth.getSession();
      
      if (!session) {
        clearInterval(testUpdateInterval);
        throw new Error('Oturum bilgisi alınamadı');
      }
      
      // Mevcut dosyaların bilgilerini al
      const { data: existingFiles, error: fetchError } = await supabase
        .from('storage_files')
        .select('file_name')
        .eq('folder_path', currentPath)
        .eq('uploaded_by', user?.id)
        .eq('is_folder', false);
      
      if (fetchError) {
        console.error('Mevcut dosyaları kontrol ederken hata:', fetchError);
      }
      
      // Dosya adlarını Set'e ekleyerek hızlı arama yapılabilir
      const existingFileNames = new Set(existingFiles?.map(file => file.file_name) || []);
      
      // Başarılı yüklenen dosyaların bilgilerini topla
      const uploadedFilesInfo = [];
      
      // Her dosya için
      for (let i = 0; i < uploadedFiles.length; i++) {
        const file = uploadedFiles[i];
        const fileExt = file.name.split('.').pop().toLowerCase();
        
        // Dosyanın orijinal ismi
        let originalFileName = file.name;
        
        // Aynı isimde dosya var mı kontrol et
        if (existingFileNames.has(originalFileName)) {
          // Dosya uzantısını ayrıştır
          const lastDotIndex = originalFileName.lastIndexOf('.');
          const nameWithoutExt = lastDotIndex !== -1 ? originalFileName.substring(0, lastDotIndex) : originalFileName;
          const extension = lastDotIndex !== -1 ? originalFileName.substring(lastDotIndex) : '';
          
          // Kopya sayısını belirle
          let copyNumber = 1;
          let newFileName = `${nameWithoutExt}(Kopya ${copyNumber})${extension}`;
          
          // Yeni isim mevcut dosyalarda var mı kontrol et, yoksa döngüden çık
          while (existingFileNames.has(newFileName)) {
            copyNumber++;
            newFileName = `${nameWithoutExt}(Kopya ${copyNumber})${extension}`;
          }
          
          // Yeni oluşturulan ismi kullan
          originalFileName = newFileName;
        }
        
        // Türkçe ve özel karakterleri düzenle, ancak orijinal dosya adını sakla
        const sanitizedFileName = sanitizeFileName(originalFileName);
        
        // Dosya yolu için "teira3d_" öneki ekleyerek isim oluştur
        const storageFileName = `teira3d_${sanitizedFileName}`;
        
        // console.log(`Dosya ${i+1}/${uploadedFiles.length} yükleniyor:`, file.name, formatSize(file.size));
        
        // Test güncelleme kullanılıyorsa, dosya değiştiğinde onu güncelle
        if (testUpdateInterval) {
          setUploadStats(prev => {
            const fileProgress = [...prev.fileProgress];
            // Önceki dosyaları %100 olarak işaretle
            for (let j = 0; j < i; j++) {
              if (fileProgress[j]) {
                fileProgress[j].loaded = fileProgress[j].total;
              }
            }
            return {
              ...prev,
              fileProgress
            };
          });
        }
        
        // Dosya yolunu belirle
        const storagePath = currentPath === '/' 
          ? `${user.id}/${storageFileName}` 
          : `${user.id}/${currentPath.substring(1)}${storageFileName}`;
        
        // Supabase client ile yükleme işlemi
        try {
          const { error: uploadError } = await storageClient.storage
            .from(BUCKET_NAME)
            .upload(storagePath, file, {
              cacheControl: '3600',
              upsert: false
            });
            
          if (uploadError) {
            console.error('Supabase SDK yükleme hatası:', uploadError);
            throw uploadError;
          }
          
          // Yükleme başarılı, dosya ilerleme durumunu tamamlandı olarak işaretle
          setUploadStats(prev => {
            const fileProgress = [...prev.fileProgress];
            fileProgress[i] = { loaded: file.size, total: file.size };
            
            // Toplam yüklenen boyutu güncelle
            const loadedSize = fileProgress.reduce((sum, file) => sum + (file?.loaded || 0), 0);
            
            return {
              ...prev,
              fileProgress,
              loadedSize
            };
          });
          
          // Dosya bilgilerini veritabanına kaydet
          const { error: dbError } = await supabase
            .from('storage_files')
            .insert({
              file_name: originalFileName, // Güncellenmiş orijinal dosya adını kullan 
              file_path: storagePath,
              file_size: file.size,
              file_type: getFileType(originalFileName),
              folder_path: currentPath, // Mevcut klasör yolu
              uploaded_by: user?.id,
              is_folder: false,
              parent_folder_id: null,
            });
            
          if (dbError) {
            clearInterval(testUpdateInterval);
            console.error('Veritabanı kayıt hatası:', dbError);
            throw dbError;
          }
          
          // Dosya ismi veritabanına kaydedildiğinde, bu ismi mevcut dosya listesine ekle
          // böylece sonraki dosyalar için kontrolde dikkate alınır
          existingFileNames.add(originalFileName);
          
          // Başarıyla yüklenen dosyayı listeye ekle
          uploadedFilesInfo.push({
            name: originalFileName,
            size: file.size,
            type: getFileType(originalFileName)
          });
          
          // console.log(`Dosya ${i+1}/${uploadedFiles.length} yüklendi:`, originalFileName);
          
        } catch (error) {
          clearInterval(testUpdateInterval);
          console.error(`Dosya ${i+1} yükleme hatası:`, error);
          throw error;
        }
      }
      
      // Test güncellemelerini temizle
      clearInterval(testUpdateInterval);
      
      // Yükleme tamamlandı, son kez istatistikleri güncelle
      setUploadProgress(100);
      setUploadStats(prev => ({
        ...prev,
        loadedSize: prev.totalSize,
        speed: prev.totalSize / ((Date.now() - startTime) / 1000),
        remainingTime: 0,
        elapsedTime: (Date.now() - startTime) / 1000,
        fileProgress: prev.fileProgress.map(file => ({ 
          loaded: file.total, 
          total: file.total 
        })),
        progress: 100,
      }));
      
      // console.log('Yükleme tamamlandı');
      
      // Başarılı mesajı göster
      toast.success(`${uploadedFiles.length} dosya başarıyla yüklendi`);
      
      // Yükleme tamamlandı durumunu ve dosya bilgilerini ayarla
      setUploadComplete(true);
      setUploadedFilesInfo(uploadedFilesInfo);
      
      // Dosya listesini yeniden yükle
      await fetchFilesFromDB();
      
      // İstatistikleri güncelle
      await fetchStorageStats();
      
      // Klasör boyutlarını güncelle
      await updateAllFolderSizes();
      
    } catch (error) {
      console.error('Dosya yüklenirken hata oluştu:', error);
      toast.error('Dosya yüklenirken bir hata oluştu');
    } finally {
      setUploadLoading(false);
      setIsUploading(false);
      setUploadFiles([]);
      setUploadComplete(true); // Yükleme tamamlandı
      
      // Yükleme istatistiklerini sıfırla
      setUploadStats({
        startTime: null,
        totalSize: 0,
        loadedSize: 0,
        speed: 0,
        remainingTime: 0,
        elapsedTime: 0,
        fileProgress: [],
        speedData: [],
        lastUpdated: 0,
        progress: 0,
      });
      
      setUploadProgress(0);
      
      // İstatistikleri güncelle
      fetchStorageStats();
    }
  };

  // Onaylama işlemi için yeni fonksiyon
  const handleConfirmUpload = () => {
    // Onay sonrası herşeyi resetle ve modalı kapat
    setUploadProgress(0);
    setUploadFiles([]);
    setUploadComplete(false);
    setUploadedFilesInfo([]);
    
    // İstatistikleri sıfırla
    setUploadStats({
      startTime: null,
      totalSize: 0,
      loadedSize: 0,
      speed: 0,
      remainingTime: 0,
      elapsedTime: 0,
      fileProgress: [],
      speedData: [],
      lastUpdated: 0,
      progress: 0,
    });
    
    // Modalı kapat
    setShowUploadModal(false);
  };

  // Vazgeç işlemi için fonksiyon
  const handleCancelUpload = () => {
    // Upload işlemi iptal edildiğinde yapılacaklar
    setUploadProgress(0);
    setUploadFiles([]);
    setUploadComplete(false);
    setUploadedFilesInfo([]);
    
    // İstatistikleri sıfırla
    setUploadStats({
      startTime: null,
      totalSize: 0,
      loadedSize: 0,
      speed: 0,
      remainingTime: 0,
      elapsedTime: 0,
      fileProgress: [],
      speedData: [],
      lastUpdated: 0,
      progress: 0,
    });
    
    // Modalı kapat
    setShowUploadModal(false);
  };

  // Dosya silme işlemi
  const handleDeleteFile = async (file) => {
    try {
      if (file.is_folder) {
        // Klasör silme işlemi - önce içindeki tüm dosya ve klasörleri bul
        toast.loading('Klasör içeriği kontrol ediliyor...', { id: 'folder-delete' });
        
        // Klasör içindeki tüm dosya ve alt klasörleri al
        const folderPath = file.file_path; // Örnek: /klasor_adi/
        
        // Recursive olarak klasörün içindeki tüm dosya ve klasörleri bul
        const { data: subItems, error: listError } = await supabase
          .from('storage_files')
          .select('*')
          .eq('uploaded_by', user.id)
          .like('file_path', `${folderPath}%`);
        
        if (listError) throw listError;

        // Klasörün içindeki tüm dosyaları (klasörler hariç) bul 
        const subFiles = subItems.filter(item => !item.is_folder);
        
        // 1. İLK OLARAK: BUCKET'TAN SİLME İŞLEMLERİ
        // Önce bucket'tan klasör ve içeriğini silme
        toast.loading(`Klasör ve içeriği bucket'tan siliniyor...`, { id: 'folder-delete' });
        
        // BUCKET YOL FORMATLARINI DOĞRU HAZIRLA
        // Kullanıcı ID'sini dosya yolundan çıkar (eğer başındaysa)
        const storageBasePath = file.file_path.includes(user.id) 
          ? file.file_path 
          
          : `${user.id}${file.file_path.startsWith('/') ? file.file_path : '/' + file.file_path}`;
        
        // Yolu doğru formatta hazırla (başında slash olmadan)
        const cleanStoragePath = storageBasePath.startsWith('/') ? storageBasePath.substring(1) : storageBasePath;
        // console.log(file.file_path);
        // console.log('Silinecek klasör yolu (storage):', cleanStoragePath);
        
        // Tüm dosyaları bucket'tan recursive olarak silen yardımcı fonksiyon
        const deleteFromBucket = async (path) => {
          // Path formatını düzelt (başında slash olmadan)
          const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
          // Sonundaki slash'i temizle, gerekirse
          const cleanPath = normalizedPath.endsWith('/') ? normalizedPath.slice(0, -1) : normalizedPath;
          
          try {
            // console.log(`Bucket içeriği listeleniyor: "${cleanPath}"`);
            
            // Bu klasör veya alt klasördeki dosyaları listele
            const { data: bucketItems, error: listError } = await storageClient.storage
              .from(BUCKET_NAME)
              .list(cleanPath, {
                limit: 1000,  // Daha fazla öğe listele
                offset: 0,
                sortBy: { column: 'name', order: 'asc' }
              });
            
            if (listError) {
              console.warn(`"${cleanPath}" listelerken hata:`, listError);
              return [];
            }
            
            if (!bucketItems || bucketItems.length === 0) {
              // console.log(`"${cleanPath}" içinde öğe bulunamadı`);
              return [];
            }
            
            // console.log(`"${cleanPath}" içinde ${bucketItems.length} öğe bulundu`);
            
            // Dosyaları ve alt klasörleri işle
            const itemsToDelete = [];
            const subfolderPromises = [];
            
            for (const item of bucketItems) {
              // Tam dosya yolu oluştur
              const itemPath = cleanPath ? `${cleanPath}/${item.name}` : item.name;
              
              if (!item.metadata) {
                // Bu bir alt klasör, recursive olarak içini listele
                // console.log(`Alt klasör işleniyor: "${itemPath}"`);
                subfolderPromises.push(deleteFromBucket(itemPath));
              } else {
                // Bu bir dosya, direkt silme listesine ekle
                itemsToDelete.push(itemPath);
                //console.log(`Dosya silme listesine eklendi: "${itemPath}"`);
              }
            }
            
            // Alt klasörleri recursive olarak işle
            const nestedItems = await Promise.all(subfolderPromises);
            const allNestedPaths = nestedItems.flat();
            
            // Tüm listeleri birleştir
            return [...itemsToDelete, ...allNestedPaths];
          } catch (error) {
            console.warn(`"${cleanPath}" işlenirken hata:`, error);
            return [];
          }
        };
        
        // Klasör içeriğini listele ve tüm dosyaları bul
        const allBucketPaths = await deleteFromBucket(cleanStoragePath);
        
        //console.log(`Silinecek toplam ${allBucketPaths.length} öğe:`, allBucketPaths);
        
        // Tüm dosyaları bucket'tan sil
        if (allBucketPaths.length > 0) {
          // Büyük dosya listelerini parçalara ayırarak sil (Supabase limitleri nedeniyle)
          const chunkSize = 100;
          for (let i = 0; i < allBucketPaths.length; i += chunkSize) {
            const chunk = allBucketPaths.slice(i, i + chunkSize);
            // console.log(`${i+1} - ${i+chunk.length} arası dosyalar siliniyor...`);
            
            const { error: removeError } = await storageClient.storage
              .from(BUCKET_NAME)
              .remove(chunk);
              
            if (removeError) {
              console.warn('Bucket parçası silinirken hata:', removeError);
            }
          }
          
          console.log('Dosyalar başarıyla silindi');
        }
        
        // Klasörün kendisini sil (üst klasörler de dahil)
        try {
          // Normalize klasör yolunu sil
          // console.log(`Klasörün kendisini silme girişimi: "${cleanStoragePath}"`);
          
          // Klasörü direkt sil
          const { error: folderError } = await storageClient.storage
            .from(BUCKET_NAME)
            .remove([cleanStoragePath]);
          
          if (folderError) {
            console.warn('Klasör silinirken hata:', folderError);
          } else {
            console.log('Klasör başarıyla silindi');
          }
          
          // Klasörün üst klasörlerini de silmeyi dene
          const pathParts = cleanStoragePath.split('/');
          if (pathParts.length > 1) {
            // En son kısmı çıkarıp üst klasörü bul
            pathParts.pop();
            const parentPath = pathParts.join('/');
            
            // console.log(`Üst klasörü silme denemesi: "${parentPath}"`);
            
            await storageClient.storage
              .from(BUCKET_NAME)
              .remove([parentPath]);
          }
        } catch (folderError) {
          // console.log('Klasör silinirken uyarı (önemsiz olabilir):', folderError);
        }
        
        // 2. SONRAKİ İŞLEM: VERİTABANINDAN SİLME İŞLEMİ
        // Klasörün kendisini ve alt klasörleri dahil tüm klasörleri veritabanından sil
        // Not: Silme işlemini en alt klasörlerden başlayarak yapmalıyız (en uzun path'den en kısaya doğru)
        const allItems = [...subItems, file];
        allItems.sort((a, b) => b.file_path.length - a.file_path.length);
        
        toast.loading(`Klasör ve içerik veritabanından siliniyor...`, { id: 'folder-delete' });
        
        // Her bir öğeyi tek tek sil
        for (const item of allItems) {
          const { error: deleteError } = await supabase
            .from('storage_files')
            .delete()
            .eq('id', item.id);
            
          if (deleteError) throw deleteError;
        }
        
        // Başarı mesajı
        toast.success(`Klasör ve içindeki ${subFiles.length} dosya başarıyla silindi`, { id: 'folder-delete' });
        
        // Dosya listesini güncelle
        setFiles(prevFiles => prevFiles.filter(f => !f.file_path.startsWith(folderPath)));
        
        // İstatistikleri güncelle
        fetchStorageStats();
        
        // İşlemler tamamlandıktan sonra klasör boyutlarını güncelle
        setTimeout(async () => {
          try {
            await updateAllFolderSizes();
          } catch (err) {
            console.log('Klasör boyutları güncellenirken hata (önemsiz):', err);
          }
        }, 500);
      } else {
        // Normal dosya silme işlemi
        toast.loading('Dosya siliniyor...', { id: 'file-delete' });
        
        // Önce storage'dan sil
        const { error: storageError } = await storageClient.storage
          .from(BUCKET_NAME)
          .remove([file.file_path]);
          
        if (storageError) {
          console.warn('Storage silme hatası, alternatif yöntem deneniyor:', storageError);
          
          // Dosya yolu formatını düzelt ve tekrar dene
          const normalizedPath = file.file_path.startsWith('/') ? file.file_path.substring(1) : file.file_path;
          if (normalizedPath !== file.file_path) {
            try {
              const { error: retryError } = await storageClient.storage
                .from(BUCKET_NAME)
                .remove([normalizedPath]);
                
              if (retryError) throw retryError;
            } catch (err) {
              console.warn('Alternatif silme de başarısız:', err);
              throw storageError; // Orijinal hatayı fırlat
            }
          } else {
            throw storageError;
          }
        }
        
        // Sonra veritabanından sil
        const { error: dbError } = await supabase
          .from('storage_files')
          .delete()
          .eq('id', file.id);
          
        if (dbError) throw dbError;
        
        // Dosya listesini güncelle
        setFiles(prevFiles => prevFiles.filter(f => f.id !== file.id));
        toast.success('Dosya başarıyla silindi', { id: 'file-delete' });
        
        // İstatistikleri güncelle
        fetchStorageStats();
        
        // Dosya silme işlemi tamamlandıktan sonra, klasör boyutlarını güncelle
        // Önce dosyanın bulunduğu klasörü belirle
        const parentFolderPath = file.folder_path;
        
        // Klasör boyutlarını güncelle
        if (parentFolderPath) {
          setTimeout(async () => {
            try {
              await getFolderSize(parentFolderPath);
            } catch (err) {
              console.log('Klasör boyutu güncellenirken hata (önemsiz):', err);
            }
          }, 500);
        }
      }
    } catch (error) {
      console.error('Silme işleminde hata oluştu:', error);
      toast.error('Silme işlemi sırasında bir hata oluştu', { id: 'file-delete' });
    }
  };

  // Dosya indirme işlemi - Supabase Gateway timeout hatası düzeltildi
  const handleDownloadFile = async (file) => {
    try {
      // İndirme URL'si oluştur - storageService'i kullan
      const { data, error } = await storageService.getPublicUrl(file.file_path);
        
      if (error) throw error;

      // Dosyayı indir
      const downloadUrl = data.publicUrl;
      // console.log(downloadUrl);
      // Gerçek indirme işlemi için bir bağlantı oluştur
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', file.file_name); // Dosya adını belirt
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Dosya indiriliyor...');
    } catch (error) {
      console.error('Dosya indirilirken hata oluştu:', error);
      toast.error('Dosya indirilirken bir hata oluştu');
    }
  };

  // Dosya tipini okunabilir formata dönüştüren fonksiyon
  const getReadableFileType = (fileType) => {
    const typeNames = {
      document: 'Doküman',
      spreadsheet: 'Çalışma Tablosu',
      presentation: 'Sunum',
      pdf: 'PDF',
      image: 'Görsel',
      video: 'Video',
      audio: 'Ses',
      archive: 'Arşiv',
      code: 'Kod',
      executable: 'Uygulama',
      '3d': '3D Model',
      database: 'Veritabanı',
      font: 'Font',
      system: 'Sistem',
      encrypted: 'Şifreli',
      scientific: 'Bilimsel',
      folder: 'Klasör',
      other: 'Diğer'
    };
    
    return typeNames[fileType] || 'Dosya';
  };

  // Dosya Kartı Bileşeni
  const FileCard = ({ file }) => {
    const fileType = file.is_folder ? 'folder' : file.file_type || 'other';
    const FileIcon = file.is_folder ? FolderPlus : FILE_TYPES[fileType]?.icon || FILE_TYPES.other.icon;
    const iconColor = file.is_folder ? 'yellow' : FILE_TYPES[fileType]?.color || 'gray';
    const isSelected = showFileActions === file.id;

    // Klasör boyutunu dinamik olarak göster
    const [folderSize, setFolderSize] = useState(file.file_size || 0);
    const [isLoadingSize, setIsLoadingSize] = useState(file.is_folder && (!file.file_size || file.file_size === 0));
    
    // Klasör için boyut bilgisini yükle
    useEffect(() => {
      if (file.is_folder && (!file.file_size || file.file_size === 0)) {
        setIsLoadingSize(true);
        getFolderSize(file.file_path)
          .then(size => {
            setFolderSize(size);
            setIsLoadingSize(false);
          })
          .catch(() => {
            setIsLoadingSize(false);
          });
      }
    }, [file]);
    
    // Dosya uzantısını al
    const getFileExtension = (fileName) => {
      return fileName.split('.').pop().toLowerCase();
    };
    
    // Klasöre çift tıklama ile içine girme
    const handleDoubleClick = () => {
      if (file.is_folder) {
        navigateToFolder(file);
      }
    };

    // Sürüklenebilirlik için gerekli özellikler
    const handleDragStart = (e) => {
      e.dataTransfer.setData('application/json', JSON.stringify(file));
      e.dataTransfer.effectAllowed = 'copy';
    };

    return (
      <motion.div
        draggable="true"
        onDragStart={handleDragStart}
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        className={`group bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all relative cursor-move ${
          viewMode === 'grid' ? 'flex flex-col' : 'flex items-center justify-between'
        } ${file.is_folder ? 'cursor-pointer hover:bg-yellow-50 dark:hover:bg-yellow-900/20' : ''}`}
        onDoubleClick={handleDoubleClick}
      >
        <div className={`flex items-center gap-4 ${viewMode === 'grid' ? 'mb-4' : ''}`}>
          <div className={`flex-shrink-0 ${viewMode === 'grid' ? 'mx-auto' : ''}`}>
            <div className={`w-14 h-14 rounded-lg bg-${iconColor}-100 dark:bg-${iconColor}-900/30 flex items-center justify-center shadow-sm hover:shadow transition-all relative`}>
              <FileIcon className={`w-7 h-7 text-${iconColor}-600 dark:text-${iconColor}-400`} />
              {!file.is_folder && (
                <span className="absolute -bottom-1 -right-1 bg-white dark:bg-gray-800 text-xs font-bold px-1 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 shadow-sm uppercase">
                  {getFileExtension(file.file_name)}
                </span>
              )}
            </div>
          </div>
          <div className={`flex-1 min-w-0 ${viewMode === 'grid' ? 'text-center mt-2' : ''}`}>
            <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors max-w-[92%]">
              {file.file_name}
            </h3>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {isLoadingSize ? (
                  <span className="inline-flex items-center">
                    <Loader className="w-3 h-3 mr-1 animate-spin" />
                    Hesaplanıyor...
                  </span>
                ) : (
                  formatFileSize(file.is_folder ? folderSize : file.file_size)
                )}
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full bg-${iconColor}-100 dark:bg-${iconColor}-900/30 text-${iconColor}-700 dark:text-${iconColor}-300 font-medium`}>
                {getReadableFileType(fileType)}
              </span>
            </div>
          </div>
        </div>

        {viewMode === 'list' && (
          <div className="flex items-center gap-6">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {formatDate(file.created_at)}
            </div>
            <span className={`text-xs px-2 py-1 rounded-full bg-${iconColor}-100 dark:bg-${iconColor}-900/30 text-${iconColor}-700 dark:text-${iconColor}-300 font-medium`}>
              {getReadableFileType(fileType)}
            </span>
          </div>
        )}

        <div className={`flex items-center gap-2 ${viewMode === 'grid' ? 'mt-4 justify-end absolute top-2 right-2' : ''}`}>
          <button 
            onClick={() => setShowFileActions(isSelected ? null : file.id)}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-opacity file-menu-toggle"
          >
            <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {/* Dosya işlem menüsü */}
        {isSelected && (
          <div 
            ref={menuRef}
            className="absolute right-0 bottom-full mb-2 bg-white dark:bg-gray-800 shadow-lg rounded-lg border border-gray-200 dark:border-gray-700 p-1 z-10 file-action-menu"
          >
            <button 
              onClick={() => showFileDetailsModal(file)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded w-full text-left"
            >
              <Info className="w-4 h-4 text-blue-500" />
              Bilgi
            </button>
            
            {file.is_folder ? (
              <>
                <button 
                  onClick={() => navigateToFolder(file)}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded w-full text-left"
                >
                  <Eye className="w-4 h-4" />
                  Aç
                </button>
                <button 
                  onClick={async () => {
                    // Klasör boyutunu yeniden hesapla
                    setIsLoadingSize(true);
                    setFolderSize(0);
                    const newSize = await calculateFolderSize(file.file_path, user.id);
                    setFolderSize(newSize);
                    setIsLoadingSize(false);
                    toast.success("Klasör boyutu yeniden hesaplandı");
                    // Dosya listesini güncelle
                    fetchFilesFromDB();
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded w-full text-left"
                >
                  <Database className="w-4 h-4" />
                  Boyutu Hesapla
                </button>
              </>
            ) : (
              <button 
                onClick={() => handleDownloadFile(file)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded w-full text-left"
              >
                <Download className="w-4 h-4" />
                İndir
              </button>
            )}
            <button 
              onClick={() => {
                setSelectedFile(file);
                setNewFileName(file.file_name.split('.')[0]);
                setShowRenameModal(true);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded w-full text-left"
            >
              <Edit2 className="w-4 h-4 text-yellow-500" />
              Yeniden Adlandır
            </button>
            <button 
              onClick={() => handleDeleteFile(file)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded w-full text-left"
            >
              <Trash2 className="w-4 h-4" />
              Sil
            </button>

          </div>
        )}
      </motion.div>
    );
  };

  // Klasör oluşturma işlemi
  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !user) {
      toast.error('Lütfen geçerli bir klasör adı girin');
      return;
    }

    // Kullanılacak klasör yolu - eğer seçilmemişse mevcut dizini kullan
    const folderPath = selectedFolderPath || currentPath;

    // Geçersiz karakterler için regex kontrolü
    // Sadece harfler, rakamlar, tire (-) ve alt çizgi (_) karakterlerine izin ver
    const validFolderNameRegex = /^[a-zA-Z0-9_-]+$/;
    
    // Türkçe karakterler için kontrol
    const hasTurkishChars = /[ğüşıöçĞÜŞİÖÇ]/.test(newFolderName);

    // Boşluk kontrolü
    const hasSpaces = /\s/.test(newFolderName);

    // Özel karakterler için kontrol (izin verilenler hariç)
    const hasSpecialChars = /[^a-zA-Z0-9_\-]/.test(newFolderName);

    if (hasTurkishChars) {
      toast.error('Klasör adında Türkçe karakter kullanılamaz (ğ, ü, ş, ı, ö, ç)');
      return;
    }

    if (hasSpaces) {
      toast.error('Klasör adında boşluk kullanılamaz');
      return;
    }

    if (hasSpecialChars) {
      toast.error('Klasör adında sadece Latin harfler, rakamlar, tire (-) ve alt çizgi (_) kullanılabilir');
      return;
    }

    if (!validFolderNameRegex.test(newFolderName)) {
      toast.error('Geçersiz klasör adı. Sadece Latin harfler, rakamlar, tire (-) ve alt çizgi (_) kullanabilirsiniz');
      return;
    }

    try {
      // Aynı isimde klasör var mı kontrol et
      const { data: existingFolders, error: checkError } = await supabase
        .from('storage_files')
        .select('id')
        .eq('folder_path', folderPath)
        .eq('file_name', newFolderName.trim())
        .eq('is_folder', true)
        .eq('uploaded_by', user.id)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingFolders) {
        toast.error('Bu isimde bir klasör zaten mevcut');
        return;
      }

      // Yeni klasör oluştur
      const { error: createError } = await supabase
        .from('storage_files')
        .insert({
          file_name: newFolderName.trim(),
          file_path: `${folderPath}${newFolderName.trim()}/`,
          file_size: 0, // Başlangıçta 0 boyut
          file_type: 'folder',
          folder_path: folderPath,
          uploaded_by: user.id,
          is_folder: true,
          parent_folder_id: null, 
        });

      if (createError) throw createError;

      toast.success('Klasör başarıyla oluşturuldu');
      setNewFolderName('');
      setShowCreateFolderModal(false);
      
      // Klasör mevcut dizinde oluşturulduysa, doğrudan yenileme yap
      if (folderPath === currentPath) {
        fetchFilesFromDB(); // Klasör listesini güncelle
      } else {
        // Başka dizinde oluşturulduysa, başarılı mesajı göster
        toast.success(`Klasör "${folderPath}" dizininde oluşturuldu`);
      }
    } catch (error) {
      console.error('Klasör oluşturulurken hata oluştu:', error);
      toast.error('Klasör oluşturulurken bir hata oluştu');
    }
  };

  // Mevcut klasörleri getiren yardımcı fonksiyon
  const fetchAvailableFolders = async () => {
    try {
      const { data, error } = await supabase
        .from('storage_files')
        .select('file_path, file_name, folder_path')
        .eq('is_folder', true)
        .eq('uploaded_by', user?.id)
        .order('file_path', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Klasörler alınırken hata oluştu:', error);
      return [];
    }
  };

  // Modal açıldığında mevcut dizini varsayılan olarak ayarla
  useEffect(() => {
    if (showCreateFolderModal) {
      setSelectedFolderPath(currentPath);
    }
  }, [showCreateFolderModal, currentPath]);

  // Klasör Oluşturma Modalı
  const CreateFolderModal = () => {
    const [availableFolders, setAvailableFolders] = useState([]);
    const [isLoadingFolders, setIsLoadingFolders] = useState(false);

    // Modal açıldığında klasörleri getir
    useEffect(() => {
      const loadFolders = async () => {
        setIsLoadingFolders(true);
        const folders = await fetchAvailableFolders();
        
        // Root dizini ekle
        const allFolders = [{ file_path: '/', file_name: 'Ana Dizin', folder_path: '' }, ...folders];
        
        setAvailableFolders(allFolders);
        setIsLoadingFolders(false);
      };
      
      loadFolders();
    }, []);
    
    return (
    <motion.div
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={() => setShowCreateFolderModal(false)}
    >
      <motion.div
        initial={false}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl flex flex-col max-h-[90vh]"
      >
        {/* Header - Fixed at top */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Yeni Klasör Oluştur
            </h2>
            <button
              onClick={() => setShowCreateFolderModal(false)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-6">
            {/* Dizin seçici */}
            <div className="mb-4">
              <label htmlFor="folder-location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Klasör Konumu
              </label>
              {isLoadingFolders ? (
                <div className="flex items-center space-x-2">
                  <div className="h-5 w-5 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin"></div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">Klasörler yükleniyor...</span>
                </div>
              ) : (
                <select
                  id="folder-location"
                  value={selectedFolderPath}
                  onChange={(e) => setSelectedFolderPath(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value={currentPath}>Mevcut Konum: {currentPath === '/' ? 'Ana Dizin' : currentPath}</option>
                  {availableFolders
                    .filter(folder => folder.file_path !== currentPath) // Mevcut konum zaten üstte gösteriliyor
                    .map((folder, index) => (
                      <option key={index} value={folder.file_path}>
                        {folder.file_path === '/' ? 'Ana Dizin' : folder.file_path}
                      </option>
                    ))}
                </select>
              )}
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Klasörün oluşturulacağı konumu seçin. Varsayılan olarak mevcut konum kullanılır.
              </p>
            </div>

            <div className="mb-6">
              <label htmlFor="folder-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Klasör Adı
              </label>
              <input
                type="text"
                id="folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyPress={(e) => {
                  // Sadece Latin harfler, rakamlar, tire ve alt çizgi karakterlerine izin ver
                  const validChars = /^[a-zA-Z0-9_-]$/;
                  if (!validChars.test(e.key)) {
                    e.preventDefault();
                    // İsteğe bağlı: geçersiz karakter yazılmaya çalışıldığında mini bir feedback
                    toast.error('Sadece Latin harfler, rakamlar, tire (-) ve alt çizgi (_) kullanabilirsiniz', 
                      { duration: 1000, position: 'bottom-center', id: 'invalid-char' });
                  }
                }}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="new_folder"
                autoFocus
              />
              
              <div className="mt-3 text-sm text-gray-500 dark:text-gray-400 space-y-1 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-md">
                <p className="font-medium text-gray-700 dark:text-gray-300">Klasör Adı Kuralları:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Sadece Latin harfler (a-z, A-Z), rakamlar (0-9), tire (-) ve alt çizgi (_) kullanılabilir</li>
                  <li>Türkçe karakterler (ğ, ü, ş, ı, ö, ç) kullanılamaz</li>
                  <li>Boşluk ve diğer özel karakterler kullanılamaz</li>
                  <li>Örnek geçerli klasör adları: project_files, backup-2023, documents123</li>
                </ul>
              </div>
            </div>
            
            {/* Seçili konum bilgisi */}
            {selectedFolderPath && (
              <div className="mb-4 p-2 bg-gray-50 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Oluşturulacak yol:</span>{' '}
                  <span className="text-primary-600 dark:text-primary-400 break-all">
                    {selectedFolderPath === '/' 
                      ? `/${newFolderName || 'yeni_klasor'}/` 
                      : `${selectedFolderPath}${newFolderName || 'yeni_klasor'}/`}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Footer with buttons - Fixed at bottom */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setShowCreateFolderModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            >
              İptal
            </button>
            <button
              onClick={handleCreateFolder}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              Oluştur
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

  // Klasöre girme işlemi
  const navigateToFolder = (folder) => {
    const newPath = `${currentPath}${folder.file_name}/`;
    
    // Gezinme geçmişini güncelle
    const newHistory = [...pathHistory.slice(0, pathHistoryIndex + 1), newPath];
    setPathHistory(newHistory);
    setPathHistoryIndex(newHistory.length - 1);
    
    // Yeni klasöre git
    setCurrentPath(newPath);
  };

  // Geri tuşu işlemi
  const handleBack = () => {
    if (pathHistoryIndex > 0) {
      setPathHistoryIndex(pathHistoryIndex - 1);
      setCurrentPath(pathHistory[pathHistoryIndex - 1]);
    }
  };

  // İleri tuşu işlemi
  const handleForward = () => {
    if (pathHistoryIndex < pathHistory.length - 1) {
      setPathHistoryIndex(pathHistoryIndex + 1);
      setCurrentPath(pathHistory[pathHistoryIndex + 1]);
    }
  };

  // Belirli bir klasöre doğrudan git
  const navigateToPath = (path) => {
    const newHistory = [...pathHistory.slice(0, pathHistoryIndex + 1), path];
    setPathHistory(newHistory);
    setPathHistoryIndex(newHistory.length - 1);
    setCurrentPath(path);
  };

  // Ana dizine git (root)
  const navigateToRoot = () => {
    const rootPath = '/';
    const newHistory = [...pathHistory.slice(0, pathHistoryIndex + 1), rootPath];
    setPathHistory(newHistory);
    setPathHistoryIndex(newHistory.length - 1);
    setCurrentPath(rootPath);
  };

  // Filtreleme modalı
  const FilterModal = () => {
    // File type görünümünü toggle etmek için state
    const [showAllFileTypes, setShowAllFileTypes] = useState(false);
    
    // Dosya boyut aralığı gösterimi için state
    const [fileSizeDisplay, setFileSizeDisplay] = useState({
      min: formatFileSize(filters.fileSizeRange[0] * 1024 * 1024),
      max: formatFileSize(filters.fileSizeRange[1] * 1024 * 1024)
    });
    
    // Tarih aralığı gösterimi için state
    const [dateRangeDisplay, setDateRangeDisplay] = useState({
      start: new Date(filters.dateRange[0]).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }),
      end: new Date(filters.dateRange[1]).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
    });

    // İlk render'da dosya boyutu ve tarih gösterimini ayarla
    useEffect(() => {
      setFileSizeDisplay({
        min: formatFileSize(filters.fileSizeRange[0] * 1024 * 1024),
        max: formatFileSize(filters.fileSizeRange[1] * 1024 * 1024)
      });
      
      setDateRangeDisplay({
        start: new Date(filters.dateRange[0]).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }),
        end: new Date(filters.dateRange[1]).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
      });
    }, [filters.fileSizeRange, filters.dateRange]);

    // Dosya Tipleri
    const fileTypes = [
      { id: 'folder', name: 'Klasörler', icon: FolderPlus, color: 'yellow' },
      { id: 'document', name: 'Dokümanlar', icon: FileText, color: 'blue' },
      { id: 'spreadsheet', name: 'Tablolar', icon: FileSpreadsheet, color: 'green' },
      { id: 'presentation', name: 'Sunumlar', icon: File, color: 'orange' },
      { id: 'pdf', name: 'PDF', icon: FileBadge, color: 'red' },
      { id: 'image', name: 'Görseller', icon: FileImage, color: 'blue' },
      { id: 'video', name: 'Videolar', icon: FileVideo, color: 'red' },
      { id: 'audio', name: 'Ses Dosyaları', icon: FileAudio, color: 'purple' },
      { id: 'archive', name: 'Arşivler', icon: FileArchive, color: 'brown' },
      { id: 'code', name: 'Kod Dosyaları', icon: FileCode, color: 'indigo' },
      { id: 'executable', name: 'Çalıştırılabilir Dosyalar', icon: FileCog, color: 'gray' },
      { id: '3d', name: '3D Modeller', icon: Tablet, color: 'pink' },
      { id: 'other', name: 'Diğer', icon: FileQuestion, color: 'gray' }
    ];

    // Dosya tipini toggle
    const toggleFileType = (typeId) => {
      setFilters(prev => {
        const newSelectedTypes = [...prev.selectedFileTypes];
        const typeIndex = newSelectedTypes.indexOf(typeId);
        
        if (typeIndex === -1) {
          newSelectedTypes.push(typeId);
        } else {
          newSelectedTypes.splice(typeIndex, 1);
        }
        
        return {
          ...prev,
          selectedFileTypes: newSelectedTypes,
          fileType: newSelectedTypes.length > 0 ? 'custom' : 'all'
        };
      });
    };

    // Boyut aralığı değişimini handle et
    const handleSizeRangeChange = (e) => {
      const [min, max] = e.target.value.split(',').map(Number);
      const minBytes = min * 1024 * 1024; // MB'dan Bayta çevir
      const maxBytes = max * 1024 * 1024;
      
      setFilters(prev => ({
        ...prev,
        fileSizeRange: [min, max],
        fileSize: 'custom'
      }));
      
      setFileSizeDisplay({
        min: formatFileSize(minBytes),
        max: formatFileSize(maxBytes)
      });
    };

    // Tarih aralığı değişimini handle et
    const handleDateRangeChange = (e) => {
      const [start, end] = e.target.value.split(',').map(Number);
      
      setFilters(prev => ({
        ...prev,
        dateRange: [start, end],
        dateAdded: 'custom'
      }));
      
      setDateRangeDisplay({
        start: new Date(start).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }),
        end: new Date(end).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
      });
    };
    
    // Tarih filtreleri için ön tanımlı seçenekler
    const dateFilterOptions = [
      { id: 'today', label: 'Bugün', range: getDayRange(0) },
      { id: 'yesterday', label: 'Dün', range: getDayRange(1) },
      { id: 'week', label: 'Bu Hafta', range: getDayRange(7) },
      { id: 'month', label: 'Bu Ay', range: getDayRange(30) },
      { id: 'quarter', label: 'Son 3 Ay', range: getDayRange(90) },
      { id: 'year', label: 'Bu Yıl', range: getDayRange(365) },
      { id: 'all', label: 'Tüm Zamanlar', range: [dateBounds.oldest.getTime(), Date.now()] }
    ];
    
    // Belirli bir gün aralığını hesapla (bugünden X gün öncesine kadar)
    function getDayRange(days) {
      const end = new Date();
      end.setHours(23, 59, 59, 999); // Günün sonuna ayarla
      
      const start = new Date();
      start.setDate(start.getDate() - days);
      start.setHours(0, 0, 0, 0); // Günün başına ayarla
      
      return [start.getTime(), end.getTime()];
    }
    
    // Ay seçimi için fonksiyon
    const filterByMonth = (monthsAgo) => {
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      
      const start = new Date();
      start.setMonth(start.getMonth() - monthsAgo);
      start.setDate(1); // Ayın ilk günü
      start.setHours(0, 0, 0, 0);
      
      setFilters({...filters, dateRange: [start.getTime(), end.getTime()], dateAdded: `month_${monthsAgo}`});
      setDateRangeDisplay({
        start: start.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }),
        end: end.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
      });
    };

    // Tüm filtreleri sıfırla
    const handleResetFilters = () => {
      setFilters({
        fileType: 'all',
        fileSize: 'all',
        dateAdded: 'all',
        sortBy: 'name_asc',
        fileSizeRange: [0, fileSizeBounds.max / (1024 * 1024)], // MB'a çevir
        dateRange: [dateBounds.oldest.getTime(), Date.now()],
        selectedFileTypes: []
      });
      setShowFilters(false);
    };

    // Boyut filtreleri için min/max hesaplama
    const minSizeMB = Math.max(0, Math.floor(fileSizeBounds.min / (1024 * 1024)));
    const maxSizeMB = Math.ceil(fileSizeBounds.max / (1024 * 1024));

  return (
      <motion.div
        initial={false}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        onClick={() => setShowFilters(false)}
      >
        <motion.div
          initial={false}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 p-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Sliders className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              Gelişmiş Filtreler
              </h2>
              <button
              onClick={() => setShowFilters(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
              <X className="w-5 h-5" />
              </button>
            </div>
          
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Dosya Tipleri */}
            <div className="col-span-1 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
                  <File className="w-5 h-5 text-blue-500" />
                  Dosya Tipi
              </div>
                <button 
                  onClick={() => setShowAllFileTypes(!showAllFileTypes)}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center"
                >
                  {showAllFileTypes ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Daha Az Göster
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Tümünü Göster
                    </>
                  )}
                </button>
              </h3>
              
              <div className="grid grid-cols-2 gap-2">
                {fileTypes.slice(0, showAllFileTypes ? fileTypes.length : 6).map((type) => {
                  const FileTypeIcon = type.icon;
                  const isSelected = filters.selectedFileTypes.includes(type.id);
                  return (
                    <button
                      key={type.id}
                      onClick={() => toggleFileType(type.id)}
                      className={`flex items-center gap-2 p-3 rounded-lg border transition-colors ${
                        isSelected 
                          ? `bg-${type.color}-50 border-${type.color}-300 dark:bg-${type.color}-900/20 dark:border-${type.color}-800` 
                          : 'bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700'
                      }`}
                    >
                      <div className={`p-2 rounded-md bg-${type.color}-100 dark:bg-${type.color}-900/30 text-${type.color}-600 dark:text-${type.color}-400`}>
                        <FileTypeIcon className="w-4 h-4" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{type.name}</span>
                      {isSelected && (
                        <div className="ml-auto bg-primary-500 text-white p-1 rounded-full">
                          <Check className="w-3 h-3" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Sıralama Seçenekleri */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                  <ArrowDownUp className="w-5 h-5 text-purple-500" />
                  Sıralama
                </h3>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setFilters({...filters, sortBy: 'name_asc'})}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      filters.sortBy === 'name_asc' 
                        ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                        : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                    } border`}
                  >
                    İsim (A-Z)
                  </button>
                  <button
                    onClick={() => setFilters({...filters, sortBy: 'date_desc'})}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      filters.sortBy === 'date_desc' 
                        ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                        : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                    } border`}
                  >
                    Yeni Eklenmiş
                  </button>
                  <button
                    onClick={() => setFilters({...filters, sortBy: 'size_desc'})}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      filters.sortBy === 'size_desc' 
                        ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                        : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                    } border`}
                  >
                    Büyük Boyut
                  </button>
                </div>
              </div>
            </div>
            
            {/* Dosya Boyutu ve Tarih Filtresi */}
            <div className="col-span-1 space-y-6">
              {/* Dosya Boyutu Seçenekleri */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                  <Database className="w-5 h-5 text-green-500" />
                  Dosya Boyutu
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        const smallLimit = Math.min(1, maxSizeMB);
                        setFilters({...filters, fileSizeRange: [0, smallLimit], fileSize: 'small'});
                        setFileSizeDisplay({
                          min: formatFileSize(0),
                          max: formatFileSize(smallLimit * 1024 * 1024)
                        });
                      }}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        filters.fileSize === 'small' 
                          ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                          : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Küçük (&lt;1MB)
                    </button>
                    <button
                      onClick={() => {
                        const mediumLimit = Math.min(10, maxSizeMB);
                        setFilters({...filters, fileSizeRange: [1, mediumLimit], fileSize: 'medium'});
                        setFileSizeDisplay({
                          min: formatFileSize(1 * 1024 * 1024),
                          max: formatFileSize(mediumLimit * 1024 * 1024)
                        });
                      }}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        filters.fileSize === 'medium' 
                          ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                          : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Orta (1-10MB)
                    </button>
                    <button
                      onClick={() => {
                        setFilters({...filters, fileSizeRange: [10, maxSizeMB], fileSize: 'large'});
                        setFileSizeDisplay({
                          min: formatFileSize(10 * 1024 * 1024),
                          max: formatFileSize(maxSizeMB * 1024 * 1024)
                        });
                      }}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        filters.fileSize === 'large' 
                          ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                          : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Büyük (&gt;10MB)
                    </button>
                    <button
                      onClick={() => {
                        setFilters({...filters, fileSizeRange: [0, maxSizeMB], fileSize: 'all'});
                        setFileSizeDisplay({
                          min: formatFileSize(0),
                          max: formatFileSize(maxSizeMB * 1024 * 1024)
                        });
                      }}
                      className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                        filters.fileSize === 'all' 
                          ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                          : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                      }`}
                    >
                      Tüm Boyutlar
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Yüklenme Tarihi */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
                  <CalendarRange className="w-5 h-5 text-orange-500" />
                  Yüklenme Tarihi
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                  <div className="grid grid-cols-3 gap-2">
                    {dateFilterOptions.slice(0, 6).map(option => (
                      <button
                        key={option.id}
                        onClick={() => {
                          setFilters({...filters, dateRange: option.range, dateAdded: option.id});
                          setDateRangeDisplay({
                            start: new Date(option.range[0]).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }),
                            end: new Date(option.range[1]).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })
                          });
                        }}
                        className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                          filters.dateAdded === option.id 
                            ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                            : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  
                  {/* Ay Filtreleri */}
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Aylara Göre</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {[1, 2, 3, 6, 9, 12].map(month => (
                        <button
                          key={`month_${month}`}
                          onClick={() => filterByMonth(month)}
                          className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                            filters.dateAdded === `month_${month}` 
                              ? 'bg-primary-100 border-primary-300 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400' 
                              : 'bg-white border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {month} {month === 1 ? 'Ay' : 'Ay'}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-between">
            {/* Filtre aktif mi kontrol et ve butonu koşullu göster */}
            {(filters.fileType !== 'all' || 
              filters.fileSize !== 'all' || 
              filters.dateAdded !== 'all' || 
              filters.sortBy !== 'name_asc' || 
              filters.selectedFileTypes.length > 0) && (
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg"
              >
                Filtreleri Temizle
              </button>
            )}
            {/* Eğer hiç filtre yoksa butonun yerini boş div ile doldur */}
            {(filters.fileType === 'all' && 
              filters.fileSize === 'all' && 
              filters.dateAdded === 'all' && 
              filters.sortBy === 'name_asc' && 
              filters.selectedFileTypes.length === 0) && (
              <div></div>
            )}
            
            <div className="space-x-2">
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 rounded-lg"
              >
                İptal
              </button>
              <button
                onClick={() => setShowFilters(false)}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
              >
                Filtreleri Uygula
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };
  
  // ... existing code ...
  
  // Sayfalama için değişkenler
  const [currentPage, setCurrentPage] = useState(1);
  const filesPerPage = 20; // Her sayfada gösterilecek dosya sayısı
  
  // Mevcut sayfada gösterilecek dosyaları hesapla
  const currentFiles = useMemo(() => {
    const indexOfLastFile = currentPage * filesPerPage;
    const indexOfFirstFile = indexOfLastFile - filesPerPage;
    return sortedFilteredFiles.slice(indexOfFirstFile, indexOfLastFile);
  }, [sortedFilteredFiles, currentPage, filesPerPage]);
  
  // Toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(sortedFilteredFiles.length / filesPerPage);
  
  // Sayfa değiştirme işlevi
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  // ... existing code ...
  
  // İstatistik bilgileri için state
  const [storageStats, setStorageStats] = useState({
    totalSize: 0,
    usedSpace: 0,
    freeSpace: 0,
    totalFiles: 0,
    totalFolders: 0,
    fileTypeDistribution: {},
    uploadHistory: [],
    largestFiles: [],
    recentActivity: [],
    sharedStats: {
      totalSharedByMe: 0,
      totalSharedWithMe: 0,
      totalPublic: 0
    }
  });
  
// İstatistik verilerini hesaplama ve getirme işlevi
const fetchStorageStats = useCallback(async () => {
  if (!user) return;

  try {
    // 1) Toplam aktif kullanıcı sayısını al
    const { count: totalUsers, error: usersError } = await supabase
      .from('user_profiles')
      .select('*', { head: true, count: 'exact' });
    if (usersError) throw usersError;

    // 2) Toplam depolama limiti (bytes cinsinden)
    const TOTAL_STORAGE_BYTES = 3875 * 1024 * 1024 * 1024; // ≈4 TB

    // 3) Kullanıcı başına düşen kota
    const perUserLimit = TOTAL_STORAGE_BYTES / (totalUsers || 1);

    // 4) Kullanıcının tüm dosya/veri kayıtlarını getir
    const { data: allFilesData, error: filesError } = await supabase
      .from('storage_files')
      .select('*')
      .eq('uploaded_by', user.id);
    if (filesError) throw filesError;
    if (!allFilesData) return;

    // 5) Dosya/klasör sayılarını ayıkla
    const totalFolders = allFilesData.filter(f => f.is_folder).length;
    const filesOnly    = allFilesData.filter(f => !f.is_folder);
    const totalFiles   = filesOnly.length;

    // 6) Dosya boyut ve tarih sınırları (filtreler için)
    if (filesOnly.length > 0) {
      const sizes = filesOnly.map(f => f.file_size || 0);
      const minSize   = Math.min(...sizes);
      const maxSize   = Math.max(...sizes);
      const dates     = filesOnly.map(f => new Date(f.created_at).getTime());
      const oldest    = new Date(Math.min(...dates));
      const newest    = new Date();

      setFileSizeBounds({
        min: minSize,
        max: maxSize > 0 ? maxSize : 100 * 1024 * 1024
      });
      setDateBounds({ oldest, newest });

      setFilters(prev => ({
        ...prev,
        fileSizeRange: [
          Math.max(0, Math.floor(minSize / (1024 * 1024))),
          Math.ceil(maxSize / (1024 * 1024))
        ],
        dateRange: [oldest.getTime(), newest.getTime()]
      }));
    }

    // 7) Kullanıcının şu ana kadar kullandığı alan
    const usedSpace = filesOnly.reduce((sum, f) => sum + (f.file_size || 0), 0);

    // 8) Kalan boş alan
    const freeSpace = Math.max(0, perUserLimit - usedSpace);

    // 9) Dosya türlerine göre dağılım
    const fileTypeDistribution = filesOnly.reduce((acc, f) => {
      const t = f.file_type || 'other';
      acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});

    // 10) Son 5 aktivite ve en büyük 5 dosya
    const recentActivity = [...filesOnly]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);

    const largestFiles = [...filesOnly]
      .sort((a, b) => (b.file_size || 0) - (a.file_size || 0))
      .slice(0, 5);

    // 11) Altı ay içindeki yüklemeleri aylık özetle
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const uploadsByMonth = filesOnly
      .filter(f => new Date(f.created_at) >= sixMonthsAgo)
      .reduce((acc, f) => {
        const d = new Date(f.created_at);
        const key = `${d.getMonth()+1}/${d.getFullYear()}`;
        if (!acc[key]) {
          acc[key] = { count: 0, size: 0, month: d.toLocaleString('tr-TR',{month:'long'}), year: d.getFullYear() };
        }
        acc[key].count += 1;
        acc[key].size  += (f.file_size || 0);
        return acc;
      }, {});
    const uploadHistory = Object.values(uploadsByMonth)
      .sort((a, b) => new Date(`${a.month} ${a.year}`) - new Date(`${b.month} ${b.year}`));

    // 12) Paylaşım istatistikleri
    let sharedByMeCount = 0, sharedWithMeCount = 0, publicFilesCount = 0;
    try {
      const [{ data: sharedByMe }, { data: sharedWithMe }, { data: publicFiles }]
        = await Promise.all([
          supabase.from('shared_files').select('id').eq('shared_by', user.id),
          supabase.from('file_shares').select('id').eq('user_id', user.id),
          supabase.from('shared_files').select('id').eq('share_type','public')
        ]);
      sharedByMeCount     = sharedByMe?.length     || 0;
      sharedWithMeCount   = sharedWithMe?.length   || 0;
      publicFilesCount    = publicFiles?.length    || 0;
    } catch (e) {
      console.error('Paylaşım istatistikleri alınırken hata:', e);
    }

    // 13) State'e kaydet
    setStorageStats({
      totalSize:        perUserLimit,
      usedSpace,
      freeSpace,
      totalFiles,
      totalFolders,
      fileTypeDistribution,
      uploadHistory,
      largestFiles,
      recentActivity,
      sharedStats: {
        totalSharedByMe:   sharedByMeCount,
        totalSharedWithMe: sharedWithMeCount,
        totalPublic:       publicFilesCount
      }
    });

  } catch (error) {
    console.error('İstatistikler getirilirken hata oluştu:', error);
    toast.error('İstatistikler yüklenirken bir hata oluştu');
  }
}, [user]);

  
  // İstatistik verilerini yükle
  useEffect(() => {
    if (user) {
      fetchStorageStats();
    }
  }, [fetchStorageStats, user]);
  
  // Dosya yükleme ve silme işlemlerinden sonra istatistikleri güncelle
  useEffect(() => {
    if (user) {
      // Dosyalar değiştiğinde istatistikleri güncelle
      fetchStorageStats();
    }
  }, [files, fetchStorageStats, user]);
  
  // ... existing code ...
  
  // İstatistikler Modalı
  const StatsModal = () => (
    <motion.div
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={() => setShowStats(false)}
    >
      <motion.div
        initial={false}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 p-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            Depolama İstatistikleri
          </h2>
          <button
            onClick={() => setShowStats(false)}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Depolama Kullanımı */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-500" />
              Depolama Kullanımı
            </h3>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-5">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600 dark:text-gray-300">
                  {formatFileSize(storageStats.usedSpace)} / {formatFileSize(storageStats.totalSize)} kullanılıyor
                </span>
                <span className="text-gray-600 dark:text-gray-300 font-medium">
                  {Math.round((storageStats.usedSpace / storageStats.totalSize) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                <div
                  className="bg-primary-600 h-2.5 rounded-full"
                  style={{ width: `${(storageStats.usedSpace / storageStats.totalSize) * 100}%` }}
                ></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Toplam Dosya</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{storageStats.totalFiles}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Toplam Klasör</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{storageStats.totalFolders}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Paylaşım İstatistikleri */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-purple-500" />
              Paylaşım İstatistikleri
            </h3>
            <div className="bg-purple-50 dark:bg-purple-900/10 rounded-lg p-5 border border-purple-100 dark:border-purple-900/30">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Paylaştığım Dosyalar</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{storageStats.sharedStats.totalSharedByMe}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Benimle Paylaşılanlar</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{storageStats.sharedStats.totalSharedWithMe}</p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Herkese Açık Dosyalar</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{storageStats.sharedStats.totalPublic}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Dosya Türleri Dağılımı */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-green-500" />
              Dosya Türleri Dağılımı
            </h3>
            <div className="space-y-3">
              {Object.entries(storageStats.fileTypeDistribution)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count]) => (
                  <div key={type} className="flex items-center">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mr-2">
                      <div
                        className={`h-2.5 rounded-full ${
                          type === 'image' ? 'bg-blue-500' :
                          type === 'document' ? 'bg-yellow-500' :
                          type === 'video' ? 'bg-red-500' :
                          type === 'audio' ? 'bg-purple-500' :
                          type === 'archive' ? 'bg-orange-500' :
                          type === 'folder' ? 'bg-green-500' :
                          type === 'pdf' ? 'bg-pink-500' :
                          type === 'code' ? 'bg-indigo-500' : 'bg-gray-500'
                        }`}
                        style={{ width: `${(count / storageStats.totalFiles) * 100}%` }}
                      ></div>
                    </div>
                    <div className="min-w-[120px] text-right">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {type.charAt(0).toUpperCase() + type.slice(1)} ({count})
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
          
          {/* Zaman İçindeki Aktivite */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Activity className="w-5 h-5 text-purple-500" />
              Aylık Aktivite
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={storageStats.uploadHistory.map(month => ({
                    name: month.month.substring(0, 3),
                    Dosya: month.count,
                    Boyut: Math.round(month.size / (1024 * 1024)) // MB cinsinden
                  }))}
                  margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis 
                    yAxisId="left"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={value => `${value}`}
                    label={{ 
                      value: 'Dosya Sayısı', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle', fontSize: 12, fill: '#666' }
                    }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickFormatter={value => `${value} MB`}
                    label={{ 
                      value: 'Toplam Boyut (MB)', 
                      angle: 90, 
                      position: 'insideRight',
                      style: { textAnchor: 'middle', fontSize: 12, fill: '#666' }
                    }}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'Boyut') return [`${value} MB`, 'Toplam Boyut'];
                      return [value, 'Dosya Sayısı'];
                    }}
                    labelFormatter={label => `${label}`}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '0.375rem',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                  />
                  <Legend 
                    verticalAlign="top" 
                    height={36}
                    formatter={(value) => {
                      if (value === 'Dosya') return 'Dosya Sayısı';
                      if (value === 'Boyut') return 'Toplam Boyut (MB)';
                      return value;
                    }}
                  />
                  <Bar 
                    yAxisId="left" 
                    dataKey="Dosya" 
                    fill="#8884d8" 
                    radius={[4, 4, 0, 0]} 
                    barSize={30}
                    name="Dosya Sayısı"
                  />
                  <Bar 
                    yAxisId="right" 
                    dataKey="Boyut" 
                    fill="#82ca9d" 
                    radius={[4, 4, 0, 0]} 
                    barSize={30}
                    name="Toplam Boyut"
                  />
                </BarChart>
              </ResponsiveContainer>
              </div>
          </div>
          
          {/* En Büyük Dosyalar */}
          <div className="col-span-1 md:col-span-2 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <Layers className="w-5 h-5 text-orange-500" />
              En Büyük Dosyalar
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dosya Adı</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tür</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Boyut</th>
                    <th className="py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Yüklenme Tarihi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {storageStats.largestFiles.map((file, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="py-2 text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                        {file.file_name}
                      </td>
                      <td className="py-2 text-sm text-gray-500 dark:text-gray-400">
                        {file.file_type}
                      </td>
                      <td className="py-2 text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(file.file_size)}
                      </td>
                      <td className="py-2 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(file.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 flex justify-end">
          <button
            onClick={() => setShowStats(false)}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg"
          >
            Kapat
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
  
  // ... existing code ...
  
  // Dosya detayları modalı
  const [showFileDetails, setShowFileDetails] = useState(false); // Dosya detayları modalı
  const [selectedFileDetails, setSelectedFileDetails] = useState(null); // Seçilen dosya detayları

  // Dosya detaylarını açma fonksiyonu
  const showFileDetailsModal = (file) => {
    setSelectedFileDetails(file);
    setShowFileDetails(true);
  };
  
  // Add state for rename modal
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newFileName, setNewFileName] = useState("");

  // Yeniden adlandırma işlemi için fonksiyon
  const handleRenameFile = async () => {
    if (!selectedFile || !newFileName.trim()) {
      toast.error('Lütfen geçerli bir isim girin');
      return;
    }

    try {
      setLoading(true);

      // Dosya uzantısını al (eğer dosya ise)
      const fileExtension = selectedFile.is_folder ? '' : selectedFile.file_name.split('.').pop();
      const oldPath = selectedFile.file_path;
      
      // Yeni dosya adını oluştur
      let newName = sanitizeFileName(newFileName);
      if (fileExtension) {
        newName = `${newName}.${fileExtension}`;
      }

      // Yeni dosya yolunu oluştur
      const folderPath = selectedFile.folder_path || '/';
      const newPath = `${folderPath}${newName}`;

      // Önce veritabanında aynı isimde dosya/klasör var mı kontrol et
      const { data: existingFiles } = await supabase
        .from('storage_files')
        .select('id')
        .eq('folder_path', folderPath)
        .eq('file_name', newName)
        .eq('uploaded_by', user?.id);

      if (existingFiles && existingFiles.length > 0) {
        toast.error('Bu isimde bir dosya/klasör zaten mevcut');
        return;
      }

      // Eğer dosya ise (klasör değilse) storage'daki dosyayı taşı
      if (!selectedFile.is_folder) {
        const bucketName = storageConfig.bucketName;
        
        // Dosyayı yeni adıyla kopyala
        const { error: moveError } = await storageClient.storage
          .from(bucketName)
          .move(oldPath, `${user.id}${folderPath === '/' ? '/' : folderPath}${newName}`);

        if (moveError) {
          console.error('Dosya taşıma hatası:', moveError);
          throw new Error('Dosya taşıma işlemi sırasında bir hata oluştu');
        }
      }

      // Veritabanında dosya/klasör bilgilerini güncelle
      const updateData = {
        file_name: newName,
        file_path: selectedFile.is_folder 
          ? `${newPath}/` 
          : `${user.id}${folderPath === '/' ? '/' : folderPath}${newName}`,
        updated_at: new Date().toISOString()
      };

      const { error: updateError } = await supabase
        .from('storage_files')
        .update(updateData)
        .eq('id', selectedFile.id);

      if (updateError) throw updateError;

      // Başarılı mesajı göster
      toast.success(`${selectedFile.is_folder ? 'Klasör' : 'Dosya'} başarıyla yeniden adlandırıldı`);
      
      // Modalı kapat ve state'i temizle
      setShowRenameModal(false);
      setNewFileName('');
      setSelectedFile(null);
      
      // Dosya listesini güncelle
      await fetchFilesFromDB();

    } catch (error) {
      console.error('Yeniden adlandırma hatası:', error);
      toast.error(error.message || 'Yeniden adlandırma işlemi sırasında bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Rename Modal bileşeni
  const RenameModal = () => {
    // Mevcut dosya adını al (uzantısız)
    const currentFileName = selectedFile?.is_folder 
      ? selectedFile.file_name 
      : selectedFile.file_name.split('.')[0];

    // İsimde gerçek bir değişiklik var mı kontrol et
    const hasNameChanged = newFileName.trim() !== currentFileName;
    const isValidNewName = newFileName.trim().length > 0;

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setShowRenameModal(false)}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-yellow-500" />
              {selectedFile?.is_folder ? 'Klasörü' : 'Dosyayı'} Yeniden Adlandır
            </h3>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <label htmlFor="newFileName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Değiştirilecek Dosya Adı
              </label>
              <input
                type="text"
                id="newFileName"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder={currentFileName}
                autoFocus
              />

              {/* Dosya Adı Kuralları */}
              <div className="mt-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-500" />
                  Dosya Adı Kuralları
                </h4>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    Sadece Latin harfler (a-z, A-Z), rakamlar (0-9), tire (-) ve alt çizgi (_) kullanılabilir
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-1">•</span>
                    Türkçe karakterler (ğ, ü, ş, ı, ö, ç) otomatik olarak Latin harflere dönüştürülür
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellow-500 mt-1">•</span>
                    Boşluklar otomatik olarak alt çizgiye (_) dönüştürülür
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    Özel karakterler (*?:"&lt;&gt;|\/) kullanılamaz
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-500 mt-1">•</span>
                    Dosya uzantısı (.{selectedFile?.file_name.split('.').pop()}) otomatik olarak korunur
                  </li>
                </ul>
              </div>

              {/* İsim değişikliği kontrolü için uyarı mesajı */}
              {newFileName.trim() && !hasNameChanged && (
                <p className="mt-4 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4" />
                  Yeni isim mevcut isimle aynı
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowRenameModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!loading && isValidNewName && hasNameChanged) {
                    handleRenameFile();
                  }
                }}
                disabled={loading || !isValidNewName || !hasNameChanged}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center gap-2 ${
                  loading || !isValidNewName || !hasNameChanged
                    ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    İşleniyor...
                  </>
                ) : (
                  'Kaydet'
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // ... existing code ...

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Üst Bilgi Alanı */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <HardDrive className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              Dosya Yöneticisi
            </h2>
            <div className="flex items-center mt-2">
              {/* Gezinme Kontrolleri */}
              <div className="flex items-center space-x-1 mr-3">
                <button
                  onClick={handleBack}
                  disabled={pathHistoryIndex <= 0}
                  className={`p-1.5 rounded-full ${
                    pathHistoryIndex <= 0
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title="Geri"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handleForward}
                  disabled={pathHistoryIndex >= pathHistory.length - 1}
                  className={`p-1.5 rounded-full ${
                    pathHistoryIndex >= pathHistory.length - 1
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  title="İleri"
                >
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              
              {/* Breadcrumb */}
              <nav className="flex" aria-label="Breadcrumb">
                <ol className="inline-flex items-center space-x-1 md:space-x-2">
                  <li className="inline-flex items-center">
                    <button
                      onClick={() => setCurrentPath('/')}
                      className={`inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 ${
                        currentPath === '/' ? 'text-primary-600 dark:text-primary-400 font-semibold' : ''
                      }`}
                    >
                      <Home className="w-4 h-4 mr-2" />
                      Ana Klasör
                    </button>
                  </li>
                  {pathSegments.map((folder, index) => (
                    <li key={folder.id}>
                      <div className="flex items-center">
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                        <button
                          onClick={() => navigateToBreadcrumb(index)}
                          className={`ml-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 ${
                            index === pathSegments.length - 1 ? 'text-primary-600 dark:text-primary-400 font-semibold' : ''
                          }`}
                        >
                          {folder.file_name}
              </button>
            </div>
                  </li>
                ))}
                </ol>
              </nav>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-1">
                <button
                  onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md ${
                    viewMode === 'grid'
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg ${
                    viewMode === 'list'
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>

            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Dosya ara..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-600 focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            {/* Filtre Butonu */}
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  Object.values(filters).some(value => value !== 'all' && value !== 'name_asc')
                    ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filtrele</span>
                {Object.values(filters).some(value => value !== 'all' && value !== 'name_asc') && (
                  <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-primary-600 rounded-full">
                    {Object.values(filters).filter(value => value !== 'all' && value !== 'name_asc').length}
                  </span>
                )}
              </button>
              
              {/* Filtre Modal */}
              <AnimatePresence>
                {showFilters && <FilterModal />}
              </AnimatePresence>
            </div>

            {/* İstatistikler Butonu */}
            <button
              onClick={() => setShowStats(true)}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white font-medium rounded-lg px-4 py-2 transition-colors"
            >
              <BarChart2 className="w-4 h-4" />
              <span className="hidden sm:inline">İstatistikler</span>
            </button>

            {/* Yeni Klasör Butonu - Minimal versiyon */}
            <button
              onClick={() => setShowCreateFolderModal(true)}
              className="p-2 text-gray-700 hover:text-yellow-600 dark:text-gray-300 dark:hover:text-yellow-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              title="Yeni Klasör"
            >
              <FolderPlus className="w-5 h-5" />
            </button>

            {/* Dosya Yükle Butonu - Minimal versiyon */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="p-2 text-gray-700 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              title="Dosya Yükle"
            >
              <Upload className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Depolama Kullanım Özeti */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-2 sm:mb-0">
              <Database className="w-5 h-5 text-blue-500" />
              Depolama Durumu
            </h3>
            
            <div className="flex gap-2">
              <span className="text-sm bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 px-3 py-1 rounded-full">
                {storageStats.totalFiles} Dosya
              </span>
              <span className="text-sm bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 px-3 py-1 rounded-full">
                {storageStats.totalFolders} Klasör
              </span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="col-span-1 md:col-span-2">
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {formatFileSize(storageStats.usedSpace)} / {formatFileSize(storageStats.totalSize)} kullanılıyor
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                  {Math.round((storageStats.usedSpace / storageStats.totalSize) * 100)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
                <div
                  className="bg-primary-600 h-2.5 rounded-full"
                  style={{ width: `${(storageStats.usedSpace / storageStats.totalSize) * 100}%` }}
                ></div>
              </div>
              
              {/* Son aktiviteler bölümü - Sol alt kısım */}
              <div className="mt-4 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Son Aktiviteler</h4>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Son 24 saat</span>
                </div>
                
                <div className="space-y-2 max-h-24 overflow-y-hidden custom-scrollbar">
                  {files.slice(0, 3).map(file => (
                    <div key={file.id} className="flex items-center justify-between py-1 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                      <div className="flex items-center gap-2">
                        <span className="p-1 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          {file.is_folder ? <Folder className="w-3 h-3" /> : <File className="w-3 h-3" />}
                        </span>
                        <span className="text-xs text-gray-700 dark:text-gray-300 truncate max-w-[90%]">{file.file_name}</span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{new Date(file.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                    </div>
                  ))}
                  
                  {files.length === 0 && (
                    <div className="text-center py-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Henüz aktivite yok</p>
                    </div>
                  )}
                </div>
                
                <div className="mt-2 pt-1 border-t border-gray-100 dark:border-gray-700/70">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400">Toplam aktivite</span>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{storageStats.totalFiles + storageStats.totalFolders} işlem</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 col-span-1 md:col-span-2">
              <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg border border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">Kullanılabilir</p>
                <p className="text-lg font-bold text-gray-900 dark:text-white">{formatFileSize(storageStats.freeSpace)}</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/10 p-3 rounded-lg border border-green-100 dark:border-green-900/30">
                <button
                  onClick={() => setShowStats(true)} 
                  className="text-xs text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
                >
                  Detaylı İstatistikler
                </button>
                <p className="text-lg font-bold text-green-600 dark:text-green-400">
                  {Object.keys(storageStats.fileTypeDistribution).length} Farklı Tür
                </p>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/10 p-3 rounded-lg border border-purple-100 dark:border-purple-900/30 col-span-2">
                <p className="text-xs text-purple-600 dark:text-purple-400">Paylaşım Durumu</p>
                <div className="flex items-center justify-between mt-1">
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    <span className="font-medium">{storageStats.sharedStats.totalSharedByMe}</span> paylaştığınız
                  </p>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    <span className="font-medium">{storageStats.sharedStats.totalSharedWithMe}</span> sizinle paylaşılan
                  </p>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    <span className="font-medium">{storageStats.sharedStats.totalPublic}</span> herkese açık
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        
        
        {/* Aktif filtreler gösterimi */}
        {Object.values(filters).some(value => value !== 'all' && value !== 'name_asc') && (
          <div className="mb-4 flex flex-wrap gap-2">
            {filters.fileType !== 'all' && (
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-full text-sm">
                <span>Tip: {filters.fileType === 'folder' ? 'Klasörler' : 
                  filters.fileType === 'document' ? 'Dokümanlar' :
                  filters.fileType === 'spreadsheet' ? 'Tablolar' :
                  filters.fileType === 'presentation' ? 'Sunumlar' :
                  filters.fileType === 'pdf' ? 'PDF' :
                  filters.fileType === 'image' ? 'Görseller' :
                  filters.fileType === 'video' ? 'Videolar' :
                  filters.fileType === 'audio' ? 'Ses Dosyaları' :
                  filters.fileType === 'archive' ? 'Arşivler' :
                  filters.fileType === 'code' ? 'Kod Dosyaları' :
                  filters.fileType === 'executable' ? 'Çalıştırılabilir Dosyalar' :
                  filters.fileType === '3d' ? '3D Modeller' : 'Diğer'}</span>
                <button
                  onClick={() => setFilters({...filters, fileType: 'all'})}
                  className="hover:bg-primary-100 dark:hover:bg-primary-800/40 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            
            {filters.fileSize !== 'all' && (
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-full text-sm">
                <span>Boyut: {
                  filters.fileSize === 'small' ? 'Küçük' :
                  filters.fileSize === 'medium' ? 'Orta' :
                  filters.fileSize === 'large' ? 'Büyük' : 'Çok Büyük'
                }</span>
                <button
                  onClick={() => setFilters({...filters, fileSize: 'all'})}
                  className="hover:bg-primary-100 dark:hover:bg-primary-800/40 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            
            {filters.dateAdded !== 'all' && (
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-full text-sm">
                <span>Tarih: {
                  filters.dateAdded === 'today' ? 'Bugün' :
                  filters.dateAdded === 'week' ? 'Son 7 Gün' :
                  filters.dateAdded === 'month' ? 'Son 30 Gün' : 'Son 1 Yıl'
                }</span>
                <button
                  onClick={() => setFilters({...filters, dateAdded: 'all'})}
                  className="hover:bg-primary-100 dark:hover:bg-primary-800/40 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            
            {filters.sortBy !== 'name_asc' && (
              <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 rounded-full text-sm">
                <span>Sıralama: {
                  filters.sortBy === 'name_desc' ? 'İsim (Z-A)' :
                  filters.sortBy === 'date_asc' ? 'Tarih (Eski-Yeni)' :
                  filters.sortBy === 'date_desc' ? 'Tarih (Yeni-Eski)' :
                  filters.sortBy === 'size_asc' ? 'Boyut (Küçük-Büyük)' :
                  'Boyut (Büyük-Küçük)'
                }</span>
                <button
                  onClick={() => setFilters({...filters, sortBy: 'name_asc'})}
                  className="hover:bg-primary-100 dark:hover:bg-primary-800/40 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full text-sm transition-colors"
            >
              Tümünü Temizle
            </button>
          </div>
        )}

        {/* Dosya Listesi */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <Loader className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" />
              <p className="text-gray-600 dark:text-gray-400">Dosyalar yükleniyor...</p>
            </div>
          </div>
        ) : sortedFilteredFiles.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 flex flex-col items-center justify-center">
            <FileQuestion className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Dosya Bulunamadı</h3>
            <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-4 animate-pulse">
              {searchQuery || Object.values(filters).some(value => value !== 'all' && value !== 'name_asc') ? 
                'Aramanız veya filtrelerinizle eşleşen dosya bulunamadı. Farklı arama terimi deneyin veya filtreleri temizleyin.' : 
                'Bu klasörde henüz dosya yok. Dosya yükleyerek başlayın.'}
            </p>
            {(searchQuery || Object.values(filters).some(value => value !== 'all' && value !== 'name_asc')) && (
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors mb-4"
              >
                Filtreleri Temizle
              </button>
            )}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg px-4 py-2 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Dosya Yükle
            </button>
          </div>
        ) : (
          <>
            {/* Dosya sayısı ve toplam */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {sortedFilteredFiles.length} dosya bulundu
                {currentPage > 1 && ` (${(currentPage - 1) * filesPerPage + 1}-${Math.min(currentPage * filesPerPage, sortedFilteredFiles.length)} gösteriliyor)`}
              </p>
            </div>
            
            {/* Dosya grid/list görünümü */}
            <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5' : 'space-y-3'}>
              {currentFiles.map((file) => (
              <FileCard key={file.id} file={file} />
            ))}
          </div>
            
            {/* Sayfalama */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <nav className="flex space-x-2">
                  <button
                    onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === 1
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    Önceki
                  </button>
                  
                  {Array.from({ length: Math.min(5, totalPages) }).map((_, index) => {
                    // Sayfa numaralarını hesapla
                    let pageNumber;
                    const totalVisible = Math.min(5, totalPages);
                    
                    if (totalPages <= 5) {
                      // 5 veya daha az sayfa varsa, 1'den başla
                      pageNumber = index + 1;
                    } else if (currentPage <= 3) {
                      // İlk sayfalardayken
                      pageNumber = index + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // Son sayfalardayken
                      pageNumber = totalPages - totalVisible + index + 1;
                    } else {
                      // Ortadayken
                      pageNumber = currentPage - 2 + index;
                    }
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === pageNumber
                            ? 'bg-primary-600 text-white dark:bg-primary-700'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded-md ${
                      currentPage === totalPages
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    Sonraki
                  </button>
                </nav>
        </div>
            )}
          </>
        )}
      </motion.div>
      <br /><br /><br /><br />
      {/* Paylaşılan Dosyalar Bölümü */}
      <SharedFiles />

      {/* Yükleme Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <div 
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowUploadModal(false)}
          >
            <div ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 max-h-[90vh] flex flex-col"
                style={{ width: '600px' }} // Sabit genişlik olarak 600px belirledim
              >
                {/* Başlık - Sabit */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <Upload className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      Dosya Yükle
                    </h3>
                    <button
                      onClick={uploadComplete ? handleConfirmUpload : () => setShowUploadModal(false)}
                      className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* İçerik - Kaydırılabilir */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  <div className="p-6">
                  {uploadComplete ? (
                    <div className="space-y-4">
                      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-600 dark:text-green-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm leading-5 font-medium text-green-800 dark:text-green-400">
                              Yükleme Başarılı!
                            </h3>
                            <div className="mt-1 text-sm leading-5 text-green-700 dark:text-green-300">
                              {uploadedFilesInfo.length} dosya başarıyla yüklendi.
                            </div>
                          </div>
                        </div>
                      </div>

                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                        Yüklenen Dosyalar ({uploadedFilesInfo.length})
                      </h4>
                      <div className="max-h-60 overflow-y-auto space-y-2 p-1">
                        {uploadedFilesInfo.map((file, index) => {
                          // Her dosya için ikon ve renk sınıflarını belirle
                          const fileTypeInfo = FILE_TYPES[file.type] || FILE_TYPES.other;
                          const iconColor = fileTypeInfo.color;
                          const IconComponent = fileTypeInfo.icon;
                          
                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 p-3 rounded-lg"
                            >
                              <div className="flex items-center space-x-3 truncate">
                                <div className="flex-shrink-0">
                                  {React.createElement(IconComponent, {
                                    className: `w-5 h-5 ${iconColor === 'blue' ? 'text-blue-600 dark:text-blue-400' : 
                                      iconColor === 'green' ? 'text-green-600 dark:text-green-400' : 
                                      iconColor === 'orange' ? 'text-orange-600 dark:text-orange-400' : 
                                      iconColor === 'red' ? 'text-red-600 dark:text-red-400' : 
                                      iconColor === 'pink' ? 'text-pink-600 dark:text-pink-400' : 
                                      iconColor === 'purple' ? 'text-purple-600 dark:text-purple-400' : 
                                      iconColor === 'indigo' ? 'text-indigo-600 dark:text-indigo-400' : 
                                      iconColor === 'yellow' ? 'text-yellow-600 dark:text-yellow-400' : 
                                      iconColor === 'teal' ? 'text-teal-600 dark:text-teal-400' : 
                                      iconColor === 'gray' ? 'text-gray-600 dark:text-gray-400' : 
                                      iconColor === 'violet' ? 'text-violet-600 dark:text-violet-400' : 
                                      iconColor === 'amber' ? 'text-amber-600 dark:text-amber-400' : 
                                      iconColor === 'cyan' ? 'text-cyan-600 dark:text-cyan-400' : 
                                      iconColor === 'rose' ? 'text-rose-600 dark:text-rose-400' : 
                                      iconColor === 'slate' ? 'text-slate-600 dark:text-slate-400' : 
                                      iconColor === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 
                                      'text-gray-600 dark:text-gray-400'}`
                                  })}
                                </div>
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate ">
                                  {file.name}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatSize(file.size)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : isUploading ? (
                    <div className="space-y-4">
                      <div className="flex flex-col space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium text-gray-700 dark:text-gray-300">
                            Yükleniyor... ({Math.floor(uploadProgress)}%)
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 animate-pulse">
                            {currentFileName}
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full bg-primary-600 dark:bg-primary-500 rounded-full"
                            animate={{ width: `${Math.floor(uploadProgress)}%` }}
                            transition={{ duration: 0.1 }}
                          ></motion.div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Yükleme Hızı</div>
                          <div className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                            <ArrowUp className="w-3 h-3 text-green-500" />
                            <motion.span
                              key={uploadStats.speed}
                              initial={false}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {formatSpeed(uploadStats.speed || 0)}
                            </motion.span>
                          </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Geçen Süre</div>
                          <div className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                            <Clock className="w-3 h-3 text-blue-500" />
                            <motion.span
                              key={uploadStats.elapsedTime}
                              initial={false}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {formatTime(uploadStats.elapsedTime || 0)}
                            </motion.span>
                          </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Tahmini Kalan Süre</div>
                          <div className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                            <Timer className="w-3 h-3 text-purple-500" />
                            <motion.span
                              key={uploadStats.remainingTime}
                              initial={false}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              {formatTime(uploadStats.remainingTime || 0)}
                            </motion.span>
                          </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Yüklenen / Toplam</div>
                          <div className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                            <Database className="w-3 h-3 text-orange-500" />
                            <span>{formatSize(uploadStats.loadedSize || 0)} / {formatSize(uploadStats.totalSize || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        {...getRootProps()}
                        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary-500 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors cursor-pointer"
                      >
                        <input {...getInputProps()} />
                        <div className="flex flex-col items-center justify-center space-y-3">
                          <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
                            <Upload className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              Dosyaları buraya sürükleyin veya dosya eklemek için tıklayın
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              (Maksimum dosya boyutu: 100MB)
                            </p>
                          </div>
                        </div>
                      </div>

                      {uploadFiles.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            Yüklenecek Dosyalar ({uploadFiles.length})
                          </h4>
                          <div className="max-h-60 overflow-y-auto space-y-2 p-1 custom-scrollbar">
                            {uploadFiles.map((file, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 p-2 rounded-lg"
                              >
                                <div className="flex items-center space-x-2 truncate">
                                  <File className="flex-shrink-0 w-4 h-4 text-gray-500 dark:text-gray-400" />
                                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                                    {file.name}
                                  </span>
                                </div>
                                <div className="flex items-center">
                                  <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                                    {formatFileSize(file.size)}
                                  </span>
                                  <button
                                    onClick={() => removeFile(index)}
                                    className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                  >
                                    <X className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  </div>
                </div>

                {/* Butonlar - Sabit */}
                {!isUploading && (
                  <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
                    {uploadComplete ? (
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={handleCancelUpload}
                          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          Vazgeç
                        </button>
                        <button
  onClick={handleConfirmUpload}
  className="px-4 py-2 text-sm font-semibold text-black bg-blue-500/10 border border-blue-400 hover:bg-blue-500/30 backdrop-blur-md rounded-xl transition-all duration-300 shadow-md hover:shadow-lg dark:text-white"
>
  Onayla
</button>

                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            setShowUploadModal(false);
                            setUploadFiles([]);
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mr-2"
                        >
                          İptal
                        </button>
                        <button
                          onClick={startUpload}
                          disabled={uploadFiles.length === 0}
                          className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                            uploadFiles.length === 0
                              ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                              : 'bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800'
                          }`}
                        >
                          Yüklemeyi Başlat
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>

      {/* Animasyonlu modallar */}
      <AnimatePresence>
        {showStats && <StatsModal />}
      </AnimatePresence>
      
      <AnimatePresence>
        {showCreateFolderModal && <CreateFolderModal />}
      </AnimatePresence>
      
      {/* Dosya Detayları Modalı */}
      <AnimatePresence>
        {showFileDetails && selectedFileDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowFileDetails(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-500" />
                  Dosya Bilgileri
                </h3>
                <button
                  onClick={() => setShowFileDetails(false)}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
              
              {/* Content */}
              <div className="p-6 space-y-5">
                {/* İkon ve Başlık */}
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {selectedFileDetails.is_folder ? (
                      <div className="w-14 h-14 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                        <FolderPlus className="w-7 h-7 text-yellow-600 dark:text-yellow-400" />
                      </div>
                    ) : (
                      <div className={`w-14 h-14 rounded-lg bg-${getFileType(selectedFileDetails.file_name) === 'other' ? 'gray' : FILE_TYPES[getFileType(selectedFileDetails.file_name)]?.color || 'gray'}-100 dark:bg-${getFileType(selectedFileDetails.file_name) === 'other' ? 'gray' : FILE_TYPES[getFileType(selectedFileDetails.file_name)]?.color || 'gray'}-900/30 flex items-center justify-center`}>
                        {React.createElement(
                          selectedFileDetails.is_folder 
                            ? FolderPlus 
                            : FILE_TYPES[getFileType(selectedFileDetails.file_name)]?.icon || FILE_TYPES.other.icon, 
                          { 
                            className: `w-7 h-7 text-${getFileType(selectedFileDetails.file_name) === 'other' ? 'gray' : FILE_TYPES[getFileType(selectedFileDetails.file_name)]?.color || 'gray'}-600 dark:text-${getFileType(selectedFileDetails.file_name) === 'other' ? 'gray' : FILE_TYPES[getFileType(selectedFileDetails.file_name)]?.color || 'gray'}-400` 
                          }
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-medium text-gray-900 dark:text-white break-all">
                      {selectedFileDetails.file_name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedFileDetails.is_folder 
                        ? 'Klasör' 
                        : selectedFileDetails.file_type 
                          ? getReadableFileType(getFileType(selectedFileDetails.file_name)) 
                          : 'Dosya'}
                    </p>
                  </div>
                </div>

                {/* Bilgiler Tablosu */}
                <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Boyut:</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {selectedFileDetails.is_folder 
                        ? formatFileSize(selectedFileDetails.file_size || 0) 
                        : formatFileSize(selectedFileDetails.file_size)}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Oluşturulma Tarihi:</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {new Date(selectedFileDetails.created_at).toLocaleString('tr-TR')}
                    </div>
                  </div>
                  {selectedFileDetails.updated_at && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Son Güncelleme:</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {new Date(selectedFileDetails.updated_at).toLocaleString('tr-TR')}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Konum:</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white break-all">
                      {selectedFileDetails.folder_path || '/'}
                    </div>
                  </div>
                  {!selectedFileDetails.is_folder && (
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Uzantı:</div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedFileDetails.file_name.split('.').pop().toUpperCase()}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="text-sm text-gray-500 dark:text-gray-400">Dosya Yolu:</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white break-all">
                      {selectedFileDetails.file_path}
                    </div>
                  </div>
                </div>

                {/* İşlem Butonları */}
                <div className="flex flex-row-reverse gap-2">
                  {!selectedFileDetails.is_folder && (
                    <button
                      onClick={() => {
                        handleDownloadFile(selectedFileDetails);
                        setShowFileDetails(false);
                      }}
                      className="px-4 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-300 flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      İndir
                    </button>
                  )}
                  {selectedFileDetails.is_folder && (
                    <button
                      onClick={() => {
                        navigateToFolder(selectedFileDetails);
                        setShowFileDetails(false);
                      }}
                      className="px-4 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-300 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      Aç
                    </button>
                  )}
                  <button
                    onClick={() => setShowFileDetails(false)}
                    className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Rename Modal */}
      <AnimatePresence>
        {showRenameModal && <RenameModal />}
      </AnimatePresence>
    </div>
  );
};

// Yükleme sırasında UI'ı güncellemek için özel komponent
const UseUploadUpdater = ({ uploadStats, setUploadStats, uploadProgress, setUploadProgress }) => {
  useEffect(() => {
    let animationFrameId;
    
    const updateUI = () => {
      // Her frame'de elapsedTime'ı güncelle (diğer değerler updateUploadStats içinde güncelleniyor)
      const now = Date.now();
      if (uploadStats.startTime) {
        const elapsedTime = (now - uploadStats.startTime) / 1000; // saniye cinsinden
        
        setUploadStats(prevStats => ({
          ...prevStats,
          elapsedTime
        }));
      }
      
      // requestAnimationFrame kullanarak tarayıcının yenileme hızında güncelleme yapıyoruz
      animationFrameId = requestAnimationFrame(updateUI);
    };
    
    animationFrameId = requestAnimationFrame(updateUI);
    
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [uploadStats.startTime, setUploadStats]);
  
  return null; // UI render etmiyoruz, sadece effect için
};

export default Files; 

